/* eslint-disable @angular-eslint/use-lifecycle-interface */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  input,
  OnDestroy,
  OnInit,
  signal,
  Signal,
  untracked,
  WritableSignal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import {
  ControlsOf,
  diff,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
} from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  ColumnRanges,
  DropdownItem,
  InputType,
  ITableChange,
  ITableColumn,
  ITableRow,
  ITableRowValue,
  ModalRef,
  ModalService,
  RowValidationList,
  RowValidationMap,
  TableRowValueList,
} from '@vinnova/ui';
import { BudgetValidation } from '@vinnova/validation';
import { formatISO } from 'date-fns';
import {
  debounceTime,
  filter,
  forkJoin,
  map,
  merge,
  Observable,
  of,
  skip,
  Subject,
  Subscription,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { Stodgrund } from '../../../../../../../formrenderer/src/lib/core/models/stodgrund.model';
import { FinancierService } from '../../../../../../../formrenderer/src/lib/core/services/financier.service';
import { Answer } from '../../../../core/models/answers/answer.model';
import { BegaranStatus } from '../../../../core/models/begaran.model';
import { FormBudget } from '../../../../core/models/form-group-mapping.model';
import { ReadonlyState } from '../../../../core/models/form.model';
import { QuestionStructureBudget } from '../../../../core/models/question/question-budget.model';
import {
  Worksite,
  WorksiteAnswer,
} from '../../../../core/models/question/question-worksite.model';
import { Question } from '../../../../core/models/question/question.model';
import { StructuredDataKey } from '../../../../core/models/question/structured-data-key.model';
import { Section } from '../../../../core/models/section.model';
import { RendererFormsQuery } from '../../../../core/queries/renderer-forms.query';
import { RendererSectionsQuery } from '../../../../core/queries/renderer-sections.query';
import { RendererFormsRepository } from '../../../../core/repositories/renderer-forms.repository';
import { BaseBegaranService } from '../../../../core/services/base-begaran.service';
import { BaseBudgetImportService } from '../../../../core/services/base-budget-import.service';
import { BaseWorksiteService } from '../../../../core/services/base-worksite.service';
import { BudgetService } from '../../../../core/services/budget.service';
import { FormValidationService } from '../../../../core/services/form-validation.service';
import { QuestionValueChangeService } from '../../../../core/services/question-value-change.service';
import { sortAlphabetically } from '../../../../shared/utils/sorting.utils';
import {
  MOBILE_BREAKPOINT,
  StickyHeaderObserver,
} from '../../../../shared/utils/sticky-header-observer.utils';
import {
  BudgetImportHelper,
  CurrentBudgetTables,
} from '../budget-import/budget-import.helper';
import { buildBudgetImportBindings } from '../budget-import/budget-import.bindings';
import { ExcelImportModalShellComponent } from '../excel-import-modal/excel-import-modal-shell.component';
import { BudgetImportApplyResult } from '../budget-import/budget-import.interface';
import {
  BudgetTableChanges,
  EffektaBudgetAnswer,
  EffektaTableData,
  EffektaTableDataMap,
  Finansiar,
  Finansieringstyp,
  IProjektBudget,
  IProjektpart,
  Kostnadsslag,
  Kostnadstyp,
  Parter,
  Projektpart,
  ProjektpartList,
  Standardkostnadstyp,
  SummaPerAr,
} from '../budget.interface';
import { MasshanteraStodgrundComponent } from '../masshantera-stodgrund/masshantera-stodgrund.component';
import {
  addRowAndColumnTotals,
  calculateYearDifference,
  findLatestDecision,
  getBudgetTableColumns,
  getProjektpartChanges,
  insertTableHeadersWithChanges,
  isEffektaSharedView,
  mergeEffektaAndUserChanges,
  mergeIncomingChanges,
  preventNonIntegerInput,
  pushTableHeadersWithChanges,
  removeLocalChanges,
  removeRestoredTableChanges,
} from '../utils/projektbidrag-budget.utils';
import {
  buildChangeMarkTooltipHtml,
  getOriginalTotalBeraknadStodniva,
  resolveChangeTooltipMeta,
  resolveOriginalFieldValue,
  resolveStodgrundTooltipValue,
  StodgrundField,
  StodgrundFieldName,
} from '../utils/stodgrund.utils';
import { Budget10kWarning } from '../validators/budget-10k-warning.validator';
import { BudgetEgenFinansieringRequired } from '../validators/budget-egen-finansiering-required.validator';
import { BudgetFinansiarRequired } from '../validators/budget-finansiar-required.validator';
import { BudgetFinansieringNotChanged } from '../validators/budget-finansiering-not-changed.validator';
import { BudgetIndirektaKostnaderWarning } from '../validators/budget-indirekta-kostnader-warning.validator';
import { BudgetKostnaderRequired } from '../validators/budget-kostnader-required.validator';
import { BudgetMaxStodnivaRequired } from '../validators/budget-max-stodniva-required.validator';
import { BudgetMaxStodnivaHigher } from '../validators/budget-maxstodniva-higher.validator';
import { BudgetMaxStodnivaLower } from '../validators/budget-maxstodniva-lower.validator';
import { BudgetMinBidrag } from '../validators/budget-min-bidrag.validator';
import { BudgetMottagareNotFinansiar } from '../validators/budget-mottagare-not-finansiar.validator';
import { BudgetStodgrundRequired } from '../validators/budget-stodgrund-required.validator';
import { BudgetVinnovaTotalFinansiering } from '../validators/budget-vinnova-total-finansiering.validator';

@UntilDestroy()
@Component({
  selector: 'lib-question-projektbidrag-budget',
  templateUrl: './projektbidrag-budget.component.html',
  styleUrls: ['./projektbidrag-budget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProjektbidragBudgetComponent implements OnInit, OnDestroy {
  KOSTNADSSLAG: ITableRow[] = [
    {
      id: Kostnadstyp.LONEKOSTNADER,
      translationKey: 'question_budget_salary_cost',
    },
    {
      id: Kostnadstyp.UTRUSTNINGSKOSTNADER,
      translationKey: 'question_budget_equipment_cost',
    },
    {
      id: Kostnadstyp.KONSULTKOSTNADER,
      translationKey: 'question_budget_consultant_cost',
    },
    {
      id: Kostnadstyp.OVRIGAKOSTNADER,
      translationKey: 'question_budget_other_cost',
    },
    {
      id: Kostnadstyp.INDIREKTAKOSTNADER,
      translationKey: 'question_budget_indirect_cost',
    },
  ];
  FINANSIERING: ITableRow[] = [
    {
      id: Finansieringstyp.VINNOVABIDRAG,
      translationKey: 'question_budget_vinnova_funding',
    },
    {
      id: Finansieringstyp.ANNANFINANSIERING,
      translationKey: 'question_budget_other_funding',
    },
    {
      id: Finansieringstyp.EGENFINANSIERING,
      translationKey: 'question_budget_own_funding',
      tooltipTextKey: 'question_budget_own_funding_tooltip',
      readonly: true,
    },
  ];
  ROWSSTANDARD = [
    {
      id: Standardkostnadstyp.TOTALKOSTNAD,
      translationKey: 'question_budget_standard_total',
    },
  ];

  questionForm = input<FormGroup<ControlsOf<FormBudget>>>();
  repeaterGroupId = input<string>();
  formBuilder = input<boolean>();
  question = input<Question>();

  readOnly$: Observable<ReadonlyState>;
  projektTimeSection: Section;
  projektPartSection: Section;
  projektStartDate: Date;
  newProjektStartDate: Date;
  projektEndDate: Date;
  newProjektEndDate: Date;
  senastBeslut: Date;
  startDate: Date;
  endDate: Date;
  InputType = InputType;
  isMobile: boolean;
  isSmallWidth: boolean;
  yearColumns: ITableColumn[];
  browseLarge: WritableSignal<ColumnRanges> = signal([]);
  browseMedium: WritableSignal<ColumnRanges> = signal([]);
  browseSmall: WritableSignal<ColumnRanges> = signal([]);
  finansieringRows: Map<string, ITableRow[]>;
  egenFinansiering: Map<string, TableRowValueList>;
  beraknadStodniva: Map<string, number>;
  originalBeraknadStodniva: Map<string, number>;
  originalTotalBeraknadStodniva: number;
  kostnaderRowValidation: RowValidationMap;
  finansieringRowValidation: RowValidationMap;
  kostnaderFormControl: Map<string, FormControl<string>>;
  finansieringFormControl: Map<string, FormControl<string>>;
  konsultkostnadOverskridenForklaringControl: Map<string, FormControl<string>>;
  sokerBidragIckeEkonomiskVerksamhetControl: Map<string, FormControl<string>>;
  isOpen: Map<string, boolean>;
  isVisible: Map<string, boolean>;
  totalKostnader: TableRowValueList;
  totalFinansiering: TableRowValueList;
  totalKostnaderFromEffekta: TableRowValueList;
  totalFinansieringFromEffekta: TableRowValueList;

  finansiarer: string[];
  parter: Parter;
  inaktivaProjektparter: ProjektpartList;
  activeLang$: Observable<string>;
  projektpartInvalid: boolean;
  projektTidInvalid: boolean;
  rapportOchSlutdatumInvalid: boolean;
  checkValidation$: Observable<boolean>;
  updateValidation$ = new Subject<boolean>();
  checkValidationSharedAnsokan = new Subject<boolean>();
  valueChangeSubscription: Subscription;
  patchValueSubscription: Subscription;
  isLoading: WritableSignal<boolean> = signal(true);
  maxStodniva: Map<string, number>;
  stodgrund: Map<string, string>;
  sfsStodgrund: Map<string, string>;
  bidragsMottagare: Set<string>;
  kostnaderChanges: Map<string, BudgetTableChanges>;
  finansieringChanges: Map<string, BudgetTableChanges>;
  showChanges: WritableSignal<boolean> = signal(false);
  hasCostChanges: Map<string, boolean> = new Map();
  hasFinansieringChanges: Map<string, boolean> = new Map();
  hasMaxStodnivaChanges: Map<string, boolean> = new Map();
  hasStodgrundChanges: Map<string, boolean> = new Map();
  hasSfsStodgrundChanges: Map<string, boolean> = new Map();
  isFirstRender: WritableSignal<boolean> = signal(true);
  budgetStandard: WritableSignal<boolean> = signal(false);
  totalKostnaderChanges: BudgetTableChanges;
  totalFinansieringChanges: BudgetTableChanges;
  massHanteraStodgrundModalRef: ModalRef<MasshanteraStodgrundComponent>;
  newProjektparter: Set<string>;
  removedProjektparter: Set<string>;
  isKonsultkostnadOverskriden: Map<string, WritableSignal<boolean>>;
  hasInactiveChanged = false;

  effektaBudgetAnswer: EffektaBudgetAnswer;
  ReadonlyState = ReadonlyState;
  isSharedView: Signal<boolean>;
  stodgrunder: Stodgrund[];
  stodgrunderItems: DropdownItem[];
  sfsStodgrunderItems;
  isChangeRequest: WritableSignal<boolean> = signal(false);
  isChangeRequestKomplettering: WritableSignal<boolean> = signal(false);
  isApplicationCompletion: WritableSignal<boolean> = signal(false);
  effektaTotalFinansiering: WritableSignal<number> = signal(undefined);

  effektaKostnader = new Map<string, EffektaTableData>();
  effektaFinansiering = new Map<string, EffektaTableData>();

  projektpartKostnader = new Map<string, EffektaTableData>();
  projektpartFinansiering = new Map<string, EffektaTableData>();
  effektaEgenFinansiering = new Map<
    string,
    { cellValues: number[]; totalRowSum: number }
  >();
  projektpartEgenFinansiering = new Map<string, { totalRowSum: number }>();
  effektaPartAnnanFinansiering = new Map<string, EffektaTableData>();
  effektaFinansiarer = new Map<string, EffektaTableData>();
  effektaTotalColumnsEgenFinansiering: number[] = [];
  effektaTotalColumnsOtherFinansiering: number[] = [];
  totalColumnsAnnanFinansiering: number[] = [];
  totalColumnsEgenFinansiering: number[] = [];
  effektaTotalEgenFinansiering = 0;
  totalOtherEffektaFinansiering = 0;
  totalOtherFinansiering = 0;
  totalEffektaKostnader: EffektaTableData;
  totalEffektaFinansiering: EffektaTableData;
  totalKostnaderSummor: EffektaTableData;
  totalFinansieringsSummor: EffektaTableData;
  totalEgenFinansiering = 0;
  totalFinansieringsSumma = 0;
  countEffektaSumsAndEgenFinasiering: boolean;
  newYearColumnCount: number;
  subscriptions = new Subscription();
  stickyHeaderObserver?: StickyHeaderObserver;
  shouldApplyIncomingBudgetChanges: Signal<boolean> = computed(
    () =>
      this.isSharedView() ||
      this.isChangeRequestKomplettering() ||
      this.isApplicationCompletion(),
  );
  canImportBudget: Signal<boolean> = computed(
    () =>
      this.budgetImportService.isSupported() &&
      !this.budgetStandard() &&
      !this.isSharedView() &&
      !this.isChangeRequest() &&
      !this.isApplicationCompletion(),
  );

  get value() {
    return this.questionForm().get('value');
  }

  constructor(
    private questionValueChangeService: QuestionValueChangeService,
    private sectionQuery: RendererSectionsQuery,
    private formsQuery: RendererFormsQuery,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private worksiteService: BaseWorksiteService,
    private translocoService: TranslocoService,
    private formValidationService: FormValidationService,
    private rendererFormRepository: RendererFormsRepository,
    private modalService: ModalService,
    private begaranService: BaseBegaranService,
    private budgetService: BudgetService,
    private financierService: FinancierService,
    private budgetImportService: BaseBudgetImportService,
  ) {
    effect(() => {
      const repeaterGroupId = this.repeaterGroupId();
      untracked(() => {
        if (this.valueChangeSubscription) {
          this.valueChangeSubscription.unsubscribe();
        }
        if (this.patchValueSubscription) {
          this.valueChangeSubscription.unsubscribe();
        }

        this.valueChangeSubscription = this.questionValueChangeService
          .valueChangeListener(
            this.value,
            {
              questionId: this.question().id,
              repeaterGroupId: this.repeaterGroupId(),
            },
            this.question(),
          )
          .pipe(untilDestroyed(this))
          .subscribe();

        this.patchValueSubscription = this.questionValueChangeService
          .patchValueListener(this.question(), repeaterGroupId, this.value)
          .pipe(untilDestroyed(this))
          .subscribe(() => {
            this.createBudgetFormControls();
            this.checkValidationSharedAnsokan.next(true);
          });
      });
    });

    this.isSharedView = toSignal(this.rendererFormRepository.isSharedView$);
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.financierService.financierAdded$
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.onFinancierAdded();
        }),
    );
    this.isChangeRequest.set(this.rendererFormRepository.getIsChangeRequest());
    this.isApplicationCompletion.set(
      this.rendererFormRepository.getIsApplicationCompletion(),
    );
    this.activeLang$ = this.translocoService.langChanges$.pipe(
      tap((lang) => {
        this.setStodgrundItems(lang);
        this.setSfsStodgrundItems(lang);
      }),
    );
    this.countEffektaSumsAndEgenFinasiering = true;
    this.readOnly$ = this.formsQuery.selectReadOnly();
    this.stodgrunder = this.budgetService.stodgrunder;

    this.rendererFormRepository.showChanges$
      .pipe(untilDestroyed(this))
      .subscribe((val) => {
        this.showChanges.set(val);
      });

    this.questionValueChangeService.patchInitialValue(
      this.question().id,
      this.repeaterGroupId(),
      this.value,
      this.question(),
    );

    if (this.isChangeRequest()) {
      this.FINANSIERING[0].translationKey =
        'question_budget_vinnova_funding_begaran';
      this.begaranService.begaran$
        .pipe(untilDestroyed(this))
        .subscribe((begaran) => {
          this.projektStartDate = begaran?.projektStartDate;
          this.projektEndDate = begaran?.projektEndDate;
          this.senastBeslut = begaran?.budgetMedAndring?.senastBeslut;
          this.isChangeRequestKomplettering.set(
            !this.isSharedView() &&
              begaran?.status === BegaranStatus.Komplettera,
          );
          if (begaran?.effektaBudgetAnswer?.value) {
            this.effektaBudgetAnswer = JSON.parse(
              begaran?.effektaBudgetAnswer.value,
            );
          }
        });
    }

    if (this.isApplicationCompletion()) {
      const effektaBudgetAnswer = this.budgetService.effektaBudgetAnswer;
      if (effektaBudgetAnswer?.value) {
        this.effektaBudgetAnswer = JSON.parse(effektaBudgetAnswer.value);
      }
    }

    if (this.isSharedView()) {
      this.FINANSIERING[0].translationKey =
        'question_budget_vinnova_funding_begaran';
      this.projektStartDate = this.budgetService.effektaProjektStartDate;
      this.projektEndDate = this.budgetService.effektaProjektEndDate;
      this.senastBeslut = this.budgetService.effektaSenastBeslut;
      this.effektaTotalFinansiering.set(
        this.budgetService.effektaTotalFinansiering,
      );
      const effektaBudgetAnswer = this.budgetService.effektaBudgetAnswer;
      if (effektaBudgetAnswer?.value) {
        this.effektaBudgetAnswer = JSON.parse(effektaBudgetAnswer.value);
      }
    }

    this.isMobile =
      /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
        navigator.userAgent,
      );
    this.isSmallWidth = window.innerWidth < MOBILE_BREAKPOINT;

    this.projektTimeSection = this.sectionQuery.getSectionWithKeyQuestion(
      StructuredDataKey.Projekttid,
    );
    if (this.isChangeRequest()) {
      const projektRapportOchSlutdatumQuestion =
        this.questionValueChangeService.getOrCreateKeyValue(
          StructuredDataKey.RapportOchSlutdatum,
        );
      if (projektRapportOchSlutdatumQuestion) {
        projektRapportOchSlutdatumQuestion
          .pipe(
            map((a: { answer: Answer; valid: boolean }) => {
              this.rapportOchSlutdatumInvalid = !a?.valid;
              return a?.answer?.value && a.valid
                ? JSON.parse(a.answer.value)
                : undefined;
            }),
            tap((value) => {
              const newProjektStart = value?.newProjectStartDate
                ? new Date(value.newProjectStartDate)
                : undefined;
              this.newProjektStartDate =
                newProjektStart?.getFullYear() >
                new Date(this.projektStartDate)?.getFullYear()
                  ? newProjektStart
                  : undefined;
              const newProjektEnd = value?.newProjectEndDate
                ? new Date(value.newProjectEndDate)
                : undefined;
              this.newProjektEndDate =
                newProjektEnd?.getFullYear() >
                new Date(this.projektEndDate)?.getFullYear()
                  ? newProjektEnd
                  : undefined;
              this.setYearColumns(value);
              this.createBudgetFormControls();
            }),
            filter(() => !!this.value.value),
            tap(() => {
              this.setValue(this.isFirstRender());
            }),
            untilDestroyed(this),
          )
          .subscribe();
      }
    } else {
      const projekttidQuestion =
        this.questionValueChangeService.getOrCreateKeyValue(
          StructuredDataKey.Projekttid,
        );
      if (projekttidQuestion) {
        projekttidQuestion
          .pipe(
            map((a: { answer: Answer; valid: boolean }) => {
              this.projektTidInvalid = !a?.valid;
              return a?.answer?.value && a.valid
                ? JSON.parse(a.answer.value)
                : undefined;
            }),
            tap((value) => {
              this.setYearColumns(value);
              this.createBudgetFormControls();
            }),
            filter(() => !!this.value.value),
            tap(() => this.setValue()),
            untilDestroyed(this),
          )
          .subscribe();
      }
    }

    const projektpartQuestion =
      this.questionValueChangeService.getOrCreateKeyValue(
        StructuredDataKey.Projektparter,
      );
    if (projektpartQuestion) {
      projektpartQuestion
        .pipe(
          switchMap((projektpart) => {
            this.projektpartInvalid = !projektpart?.valid;
            return this.loadParter(
              projektpart?.answer?.value
                ? JSON.parse(projektpart.answer.value)
                : [],
            );
          }),
          tap(() => {
            this.isLoading.set(false);
            if (
              (this.isSharedView() ||
                this.isChangeRequest() ||
                this.isApplicationCompletion()) &&
              this.countEffektaSumsAndEgenFinasiering
            ) {
              this.setStartingSumsFromEffekta();
            }
            this.createBudgetFormControls();
          }),
          filter(() => !!this.value.value),
          tap(() => {
            this.setValue(this.isFirstRender());
            this.isFirstRender.set(false);
          }),
          untilDestroyed(this),
        )
        .subscribe();
    }

    this.projektPartSection = this.sectionQuery.getSectionWithKeyQuestion(
      StructuredDataKey.Projektparter,
    );

    this.checkValidation$ = merge(
      this.formValidationService.showErrors$,
      this.checkValidationSharedAnsokan,
    );

    this.value.addValidators(BudgetMinBidrag);
    if (this.isSharedView()) {
      this.value.addValidators(BudgetMaxStodnivaRequired);
      this.value.addValidators(BudgetMaxStodnivaLower);
      this.value.addValidators(BudgetMaxStodnivaHigher);
      this.value.addValidators(
        BudgetStodgrundRequired(
          this.stodgrunder
            .filter((s) => s.paragrafer.length > 0)
            .map((s) => s.stodgrundId.toString()),
        ),
      );
    }
    this.value.updateValueAndValidity();
    this.value.touch$.pipe(take(1)).subscribe(() => {
      this.cdr.markForCheck();
    });

    if (this.isSharedView() || this.isChangeRequest()) {
      this.questionValueChangeService.sendInitialKeyValue(
        this.question(),
        this.value,
        {
          questionId: this.question().id,
          repeaterGroupId: this.repeaterGroupId(),
        },
        this.questionForm().valid,
      );
    }
  }

  ngOnDestroy() {
    this.worksiteService.resetWorksites();
    this.stickyHeaderObserver?.destroy();
    this.subscriptions.unsubscribe();
  }

  onFinancierAdded(): void {
    const projektpartQuestion =
      this.questionValueChangeService.getOrCreateKeyValue(
        StructuredDataKey.Projektparter,
      );

    if (projektpartQuestion) {
      projektpartQuestion
        .pipe(
          tap(() => {
            this.createBudgetFormControls();
          }),
          untilDestroyed(this),
        )
        .subscribe();
    }
  }

  private setStartingSumsFromEffekta() {
    if (this.effektaBudgetAnswer) {
      for (const effektaPartValue of this.effektaBudgetAnswer.projektparter) {
        const effektakostnaderValue = effektaPartValue?.kostnader
          ? this.getKostnadValueForWorksite(effektaPartValue)
          : undefined;
        const parsedEffektakostnaderValue = effektakostnaderValue
          ? JSON.parse(effektakostnaderValue)
          : undefined;

        this.setSumsEffektaValues(
          this.effektaKostnader,
          effektaPartValue.id,
          parsedEffektakostnaderValue,
          this.KOSTNADSSLAG.length,
        );

        const effektafinansieringValue = effektaPartValue
          ? this.getFinansieringValueForWorksite(effektaPartValue)
          : undefined;
        const parsedEffektaFinansieringValue = effektafinansieringValue
          ? JSON.parse(effektafinansieringValue)
          : undefined;
        this.setTotalCellValuesEffekta(
          parsedEffektakostnaderValue,
          parsedEffektaFinansieringValue,
        );

        this.setSumsEffektaValues(
          this.effektaFinansiering,
          effektaPartValue.id,
          parsedEffektaFinansieringValue,
          this.finansieringRows.get(effektaPartValue.id)?.length,
        );
        if (this.countEffektaSumsAndEgenFinasiering) {
          this.setTotalFinansieringPerAr(
            effektaPartValue.finansiarer,
            effektaPartValue.id,
          );
          const egenFinansiering = [];
          const kostnaderColumns = this.effektaKostnader.get(
            effektaPartValue.id,
          ).columns;
          const finansieringsColumns = this.effektaFinansiering.get(
            effektaPartValue.id,
          ).columns;
          const otherfinansieringsColumns =
            this.effektaPartAnnanFinansiering.get(effektaPartValue.id).columns;
          let total = 0;
          for (const [i, kostnaderColumn] of kostnaderColumns.entries()) {
            const egenFin =
              kostnaderColumn -
              finansieringsColumns[i] -
              (otherfinansieringsColumns[i] ?? 0);
            egenFinansiering.push(egenFin);
            total += egenFin;

            if (this.effektaTotalColumnsEgenFinansiering[i]) {
              this.effektaTotalColumnsEgenFinansiering[i] += egenFin;
            } else {
              this.effektaTotalColumnsEgenFinansiering[i] = egenFin;
            }
            this.effektaTotalEgenFinansiering += egenFin;
          }
          this.effektaEgenFinansiering.set(effektaPartValue.id, {
            cellValues: egenFinansiering,
            totalRowSum: total,
          });
        }
      }
    }
    this.countEffektaSumsAndEgenFinasiering = false;
    this.setTotalValuesEffekta();
  }

  private getEffektaFinansieringSumma(
    projektpartId: string,
    finansiarId: string,
  ) {
    const finansiar = this.effektaBudgetAnswer.projektparter
      ?.find((p) => p.id == projektpartId)
      ?.finansiarer.find((f) => f.id == finansiarId);

    return finansiar?.finansieringPerAr
      .map((f) => f.summa)
      .reduce((a, b) => a + b, 0);
  }

  private setTotalFinansieringPerAr(finansiarer, id) {
    const colSums = Array.from({ length: this.yearColumns.length }, () => 0);
    let summa = 0;
    for (const finansier of finansiarer) {
      for (const [i, financiering] of finansier.finansieringPerAr.entries()) {
        if (this.effektaTotalColumnsOtherFinansiering[i]) {
          this.effektaTotalColumnsOtherFinansiering[i] += financiering.summa;
        } else {
          this.effektaTotalColumnsOtherFinansiering[i] = financiering.summa;
        }
        colSums[i] += financiering.summa;
        summa += financiering.summa;
        this.totalOtherEffektaFinansiering += financiering.summa;
      }
    }
    this.effektaPartAnnanFinansiering.set(id, {
      rows: [],
      columns: colSums,
      totalSum: summa,
    });
  }

  private setSumsEffektaValues(
    effektaMap: EffektaTableDataMap,
    id: string,
    parsedEffektaValue: TableRowValueList,
    length: number,
  ) {
    if (!effektaMap) {
      effektaMap = new Map<string, EffektaTableData>();
    }

    const rowSums = Array.from({ length: length }, () => 0);
    const colSums = Array.from({ length: this.yearColumns.length }, () => 0);
    let totalSum = 0;

    for (const val of parsedEffektaValue) {
      if (+val.value) {
        rowSums[+val.rowId - 1] += +val.value;
        colSums[+val.columnId] += +val.value;
        totalSum += +val.value;
      }
    }

    effektaMap.set(id, {
      rows: rowSums ?? [],
      columns: colSums,
      totalSum: totalSum,
    });
  }

  private setTotalValuesEffekta() {
    this.totalEffektaFinansiering = { rows: [], columns: [], totalSum: 0 };
    this.totalEffektaKostnader = { rows: [], columns: [], totalSum: 0 };
    if (this.totalFinansieringFromEffekta && this.totalKostnaderFromEffekta) {
      for (const fin of this.totalFinansieringFromEffekta) {
        this.addValue(this.totalEffektaFinansiering.rows, fin);
        this.addValue(this.totalEffektaFinansiering.columns, fin, true);
      }

      for (const kostnad of this.totalKostnaderFromEffekta) {
        this.addValue(this.totalEffektaKostnader.rows, kostnad);
        this.totalEffektaKostnader.totalSum += +kostnad.value;
        this.addValue(this.totalEffektaKostnader.columns, kostnad, true);
      }
    }
  }

  private setTotalTableSums() {
    this.totalFinansieringsSummor = { rows: [], columns: [], totalSum: 0 };
    this.totalKostnaderSummor = { rows: [], columns: [], totalSum: 0 };

    for (const fin of this.totalFinansiering) {
      this.addValue(this.totalFinansieringsSummor.rows, fin);
      this.addValue(this.totalFinansieringsSummor.columns, fin, true);
      this.totalFinansieringsSummor.totalSum += +fin.value;
    }

    for (const kostnad of this.totalKostnader) {
      this.addValue(this.totalKostnaderSummor.rows, kostnad);
      this.totalKostnaderSummor.totalSum += +kostnad.value;
      this.addValue(this.totalKostnaderSummor.columns, kostnad, true);
    }
  }

  private addValue(
    rowsOrColumns: number[],
    item: ITableRowValue,
    isColumn = false,
  ) {
    const index = isColumn ? +item.columnId : +item.rowId - 1;
    if (rowsOrColumns[index] || rowsOrColumns[index] === 0) {
      rowsOrColumns[index] += +item.value;
    } else {
      rowsOrColumns[index] = +item.value;
    }
  }

  private setTotalCellValuesEffekta(
    parsedEffektakostnaderValue: TableRowValueList,
    parsedEffektaFinansieringValue: TableRowValueList,
  ) {
    if (!this.totalKostnaderFromEffekta) {
      this.totalKostnaderFromEffekta = parsedEffektakostnaderValue;
      this.totalFinansieringFromEffekta = parsedEffektaFinansieringValue.filter(
        (f) => f.rowId === '1',
      );
      return;
    }
    for (const [index, kostnad] of parsedEffektakostnaderValue.entries()) {
      this.totalKostnaderFromEffekta[index].value += kostnad.value;
    }

    for (const [index, finansiering] of parsedEffektaFinansieringValue
      .filter((f) => f.rowId === '1')
      .entries()) {
      this.totalFinansieringFromEffekta[index].value += finansiering.value;
    }
  }

  getSectionNumber(sectionId: string): string {
    return this.formsQuery.getSectionNumber(sectionId);
  }

  goToSection(sectionId: string) {
    const accordion = document.querySelector(
      `[aria-controls="accordion-${sectionId}"`,
    ) as HTMLElement;
    if (accordion.getAttribute('aria-expanded') === 'false') {
      const collapsible = document.querySelector(`#accordion-${sectionId}`);
      accordion.click();
      collapsible.addEventListener(
        'shown.bs.collapse',
        function scrollToSection() {
          accordion.scrollIntoView({ block: 'center' });
          collapsible.removeEventListener('shown.bs.collapse', scrollToSection);
        },
      );
    } else {
      accordion.scrollIntoView({ block: 'center' });
    }
  }

  loadParter(worksiteAnswers: WorksiteAnswer[]) {
    if (!worksiteAnswers.some((a) => a.isCoordinator)) {
      this.parter = undefined;
      return of(undefined);
    }
    this.parter = { koordinator: undefined, projektparter: [] };
    this.inaktivaProjektparter = [];
    this.newProjektparter = new Set<string>();
    this.removedProjektparter = new Set<string>();
    const worksitesObs: Observable<Worksite>[] = [];
    for (const worksiteAnswer of worksiteAnswers) {
      worksitesObs.push(
        this.worksiteService.getWorksite(worksiteAnswer.worksiteId),
      );
      for (const financierId of worksiteAnswer.financiers) {
        worksitesObs.push(
          this.worksiteService.getFinancier(
            financierId,
            worksiteAnswer.worksiteId,
          ),
        );
      }
    }

    return forkJoin(worksitesObs).pipe(
      tap((worksites) => {
        for (const worksiteAnswer of worksiteAnswers) {
          const financiers = [];
          const worksite = worksites.find(
            (w) => w.id === worksiteAnswer.worksiteId,
          );
          if (!worksite) {
            continue;
          }
          if (worksiteAnswer.financiers) {
            for (const financierId of worksiteAnswer.financiers) {
              const financier = this.worksiteService.getFinancierValue(
                financierId,
                worksite.id,
              );
              if (!financier) {
                continue;
              }
              financiers.push(financier);
            }
          }

          if (worksiteAnswer.isCoordinator) {
            this.parter.koordinator = { worksite, financiers };
          } else if (worksiteAnswer.isInactive) {
            this.inaktivaProjektparter.push({
              worksite,
              financiers,
            });
          } else {
            this.parter.projektparter.push({ worksite, financiers });
          }

          if (worksiteAnswer.isNew) {
            this.newProjektparter.add(worksite.id);
          }
          if (worksiteAnswer.isInactiveChanged) {
            this.removedProjektparter.add(worksite.id);
          }
          this.hasInactiveChanged = worksiteAnswer.isInactiveChanged;
        }
        this.parter.projektparter = sortAlphabetically(
          this.parter.projektparter,
        );
        // Städa bort ändringar som inte längre har en koppling till en projektpart
        const currentIds = [
          this.parter.koordinator?.worksite.id,
          ...this.parter.projektparter.map((p) => p.worksite.id),
          ...this.inaktivaProjektparter.map((p) => p.worksite.id),
        ].filter(Boolean);

        for (const key of this.hasFinansieringChanges.keys()) {
          if (!currentIds.includes(key)) {
            this.hasFinansieringChanges.delete(key);
          }
        }
        for (const key of this.hasCostChanges.keys()) {
          if (!currentIds.includes(key)) {
            this.hasCostChanges.delete(key);
          }
        }
      }),
    );
  }

  createBudgetFormControls() {
    this.kostnaderFormControl = new Map<string, FormControl<string>>();
    this.finansieringFormControl = new Map<string, FormControl<string>>();
    this.konsultkostnadOverskridenForklaringControl = new Map<
      string,
      FormControl<string>
    >();
    this.sokerBidragIckeEkonomiskVerksamhetControl = new Map<
      string,
      FormControl<string>
    >();
    this.maxStodniva = new Map<string, number>();
    this.stodgrund = new Map<string, string>();
    this.sfsStodgrund = new Map<string, string>();
    this.bidragsMottagare = new Set<string>();
    this.kostnaderChanges = new Map<string, ITableChange[][][]>();
    this.finansieringChanges = new Map<string, ITableChange[][][]>();
    this.questionForm().setControl('tables', new FormArray([]));
    this.questionForm().setControl('stodniva', new FormControl<number>());
    this.questionForm().setControl('soktBidrag', new FormControl<number>());
    this.questionForm().setControl(
      'projektPartAndDateValid',
      new FormControl<boolean>(
        !this.projektTidInvalid && !this.projektpartInvalid,
        Validators.requiredTrue,
      ),
    );
    this.questionForm().setControl(
      'konsultkostnadOverskridenForklaring',
      new FormArray([]),
    );
    this.questionForm().setControl(
      'sokerBidragIckeEkonomiskVerksamhet',
      new FormArray([]),
    );

    this.totalKostnaderChanges = [];
    this.totalFinansieringChanges = [];

    const parsedValue = this.value.value
      ? JSON.parse(this.value.value)
      : undefined;

    if (parsedValue) {
      this.budgetStandard.set(
        (parsedValue?.budgetNiva as string)?.toLowerCase() === 'standard',
      );
    } else {
      this.budgetStandard.set(
        (this.effektaBudgetAnswer?.budgetNiva as string)?.toLowerCase() ===
          'standard',
      );
    }

    if ((this.question().structure as QuestionStructureBudget).maxStodniva) {
      this.questionForm()
        .get('stodniva')
        .addValidators(
          Validators.max(
            (this.question().structure as QuestionStructureBudget).maxStodniva,
          ),
        );
      this.questionForm().get('stodniva').updateValueAndValidity();
    }
    if ((this.question().structure as QuestionStructureBudget).maxSoktBidrag) {
      this.questionForm()
        .get('soktBidrag')
        .addValidators(
          Validators.max(
            (this.question().structure as QuestionStructureBudget)
              .maxSoktBidrag,
          ),
        );
      this.questionForm().get('soktBidrag').updateValueAndValidity();
    }
    if (this.isChangeRequest()) {
      let totalFinansieringsBidrag = 0;
      if (this.effektaBudgetAnswer?.projektparter) {
        for (const projektpart of this.effektaBudgetAnswer.projektparter) {
          for (const bidrag of projektpart.bidragPerAr) {
            totalFinansieringsBidrag += bidrag.summa;
          }
        }
      }
      this.questionForm()
        .get('soktBidrag')
        .addValidators(BudgetFinansieringNotChanged(totalFinansieringsBidrag));
      this.questionForm().get('soktBidrag').updateValueAndValidity();
    }
    if (
      this.isSharedView() &&
      this.formsQuery.getReadOnly() === ReadonlyState.Editable
    ) {
      this.questionForm()
        .get('soktBidrag')
        .addValidators(
          BudgetVinnovaTotalFinansiering(this.effektaTotalFinansiering()),
        );
      this.questionForm().get('soktBidrag').updateValueAndValidity();
    }

    this.finansieringRows = new Map<string, { id: string; title: string }[]>();
    this.isOpen = new Map<string, boolean>();
    this.isVisible = new Map<string, boolean>();
    this.beraknadStodniva = new Map<string, number>();
    this.originalBeraknadStodniva = new Map<string, number>();
    this.originalTotalBeraknadStodniva =
      this.effektaBudgetAnswer?.beraknadStodnivaTotalt?.vardeDecimal;
    this.egenFinansiering = new Map<
      string,
      {
        rowId: string;
        columnId: string;
        value: number;
      }[]
    >();
    this.finansieringRowValidation = new Map<string, RowValidationList>();
    this.finansiarer = [];
    this.isKonsultkostnadOverskriden = new Map<
      string,
      WritableSignal<boolean>
    >();
    this.kostnaderRowValidation = new Map<string, RowValidationList>();

    this.resetTotalTablesChanges();

    if (this.parter?.koordinator && this.startDate && this.endDate) {
      this.createControlsForProjektpart(this.parter.koordinator, true);
      for (const projektpart of this.parter.projektparter) {
        this.createControlsForProjektpart(projektpart, false);
      }
      for (const projektpart of this.inaktivaProjektparter) {
        this.createControlsForProjektpart(projektpart, false);
      }
    }

    const hasBidragsMottagareIsFinansiarError = [
      ...this.finansieringFormControl.values(),
    ].some(
      (control) =>
        control.errors &&
        Object.keys(control.errors).some((key) =>
          key.startsWith('BudgetMottagareNotFinansiar'),
        ),
    );

    if (hasBidragsMottagareIsFinansiarError) {
      this.updateBidragsMottagareNotFinansiarForAllControls();
    }
    this.setTotals();
    this.cdr.markForCheck();
  }

  private createControlsForProjektpart(
    projektpart: Projektpart,
    isCoordinator: boolean,
  ) {
    this.isOpen.set(projektpart.worksite.id, !this.isMobile);
    this.isVisible.set(projektpart.worksite.id, !this.isMobile);

    const finansieringRow: ITableRow[] = [this.FINANSIERING[0]];
    const finansieringRowValidation: RowValidationList = [];
    for (const financier of sortAlphabetically(projektpart.financiers)) {
      finansieringRow.push({
        id: financier.id,
        title: financier.company.name,
      });
      this.finansiarer.push(financier.id);
      finansieringRowValidation.push(
        {
          rowId: financier.id,
          validation: BudgetValidation.FinansiarRequired,
        },
        {
          rowId: financier.id,
          validation: BudgetValidation.MottagareNotFinansiar,
        },
      );
    }
    finansieringRowValidation.push(
      {
        rowId: Finansieringstyp.VINNOVABIDRAG,
        validation: BudgetValidation.Under10KWarning,
      },
      {
        rowId: Finansieringstyp.EGENFINANSIERING,
        validation: BudgetValidation.EgenFinansieringRequired,
        arg: 0,
      },
    );
    finansieringRow.push(this.FINANSIERING[2]);
    this.finansieringRows.set(projektpart.worksite.id, finansieringRow);

    const inactiveWorksite = this.inaktivaProjektparter.find(
      (p) => p.worksite.id === projektpart.worksite.id,
    );
    this.finansieringRowValidation.set(
      projektpart.worksite.id,
      inactiveWorksite ? [] : finansieringRowValidation,
    );

    const kostnaderRowValidation: RowValidationList =
      this.isSharedView() || this.isChangeRequest()
        ? []
        : [
            {
              rowId: Kostnadstyp.INDIREKTAKOSTNADER,
              validation: BudgetValidation.IndirektaKostnaderOverLonekostnader,
              arg: 0,
            },
          ];
    this.kostnaderRowValidation.set(
      projektpart.worksite.id,
      inactiveWorksite ? [] : kostnaderRowValidation,
    );

    const projektpartValue = this.getProjektPartValue(projektpart);
    const effektaPartValue = this.effektaBudgetAnswer?.projektparter?.find(
      (p) => p.id === projektpart.worksite.id,
    );
    const kostnaderValue = projektpartValue?.kostnader
      ? this.getKostnadValueForWorksite(projektpartValue)
      : undefined;

    const parsedKostnaderValue: Kostnadsslag[] = kostnaderValue
      ? JSON.parse(kostnaderValue)
      : undefined;

    if (this.budgetStandard() && parsedKostnaderValue) {
      for (const [index] of parsedKostnaderValue.entries()) {
        if (index > effektaPartValue.kostnader[0].kostnaderPerAr.length - 1) {
          effektaPartValue.kostnader[0].kostnaderPerAr.push({
            ar: effektaPartValue.kostnader[0].kostnaderPerAr[index - 1].ar + 1,
            summa: 0,
            andringsmarkeringar: [],
          });
        }
      }
    }
    const finansieringValue = projektpartValue
      ? this.getFinansieringValueForWorksite(projektpartValue)
      : undefined;

    const parsedfinansieringValue = finansieringValue
      ? JSON.parse(finansieringValue)
      : undefined;

    if (
      this.isChangeRequest() ||
      this.isSharedView() ||
      this.isApplicationCompletion()
    ) {
      this.checkFinancingChanges(
        effektaPartValue,
        parsedfinansieringValue,
        projektpartValue,
      );
      this.checkCostChanges(
        effektaPartValue,
        parsedKostnaderValue,
        projektpartValue,
      );

      if (effektaPartValue === undefined && projektpartValue) {
        projektpartValue.isNew = true;
        this.checkNewProjectPartChanges(
          parsedKostnaderValue,
          parsedfinansieringValue,
          projektpartValue,
        );
      }
    }
    const kostnaderFc = this.fb.control<string>(kostnaderValue);
    const finansieringFc = this.fb.control<string>(finansieringValue);
    const konsultkostnadOverskridenForklaringFc = this.fb.control<string>(
      projektpartValue?.konsultkostnadOverskridenForklaring ?? '',
      [Validators.maxLength(1000)],
    );

    const sokerBidragIckeEkonomiskVerksamhetValidators = [Validators.required];

    const sokerBidragIckeEkonomiskVerksamhetFc = this.fb.control(
      projektpartValue?.sokerBidragIckeEkonomiskVerksamhet,
      sokerBidragIckeEkonomiskVerksamhetValidators,
    );
    this.kostnaderFormControl.set(projektpart.worksite.id, kostnaderFc);
    this.finansieringFormControl.set(projektpart.worksite.id, finansieringFc);
    this.sokerBidragIckeEkonomiskVerksamhetControl.set(
      projektpart.worksite.id,
      sokerBidragIckeEkonomiskVerksamhetFc,
    );
    this.konsultkostnadOverskridenForklaringControl.set(
      projektpart.worksite.id,
      konsultkostnadOverskridenForklaringFc,
    );
    this.isKonsultkostnadOverskriden.set(
      projektpart.worksite.id,
      signal(false),
    );
    this.setKonsultkostnaderOverstriden(projektpart.worksite.id);
    this.setSokerBidrag(projektpart.worksite.id);

    this.questionForm().get('tables').push(kostnaderFc);
    this.questionForm().get('tables').push(finansieringFc);
    this.questionForm()
      .get('konsultkostnadOverskridenForklaring')
      .push(konsultkostnadOverskridenForklaringFc);
    this.questionForm()
      .get('sokerBidragIckeEkonomiskVerksamhet')
      .push(sokerBidragIckeEkonomiskVerksamhetFc);

    this.setEgenFinansiering(projektpart.worksite.id);

    if (!inactiveWorksite) {
      this.setEgenFinansieringValidation(projektpart.worksite.id);
      this.setBudgetIndirektaKostnaderValidation(projektpart.worksite.id);
    }
    this.setBeraknadStodniva(projektpart.worksite.id);
    if (!isCoordinator && !inactiveWorksite) {
      this.setValidation(
        kostnaderFc,
        [BudgetValidation.KostnaderRequired],
        [],
        false,
      );
    }

    finansieringFc.value$
      .pipe(diff(), skip(1), untilDestroyed(this))
      .subscribe(() => {
        this.setEgenFinansiering(projektpart.worksite.id);
      });

    konsultkostnadOverskridenForklaringFc.value$
      .pipe(
        // skippar första värde som kommer från när man skapar formcontrollen
        skip(1),
        debounceTime(500),
        diff(),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.setValue();
      });

    sokerBidragIckeEkonomiskVerksamhetFc.value$
      .pipe(
        // skippar första värde som kommer från när man skapar formcontrollen
        skip(1),
        diff(),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.setValue();
      });

    this.maxStodniva.set(
      projektpart.worksite.id,
      projektpartValue?.maxStodniva?.varde,
    );
    const stodgrund =
      this.isChangeRequest() && effektaPartValue
        ? effektaPartValue?.stodgrund?.varde?.toString()
        : projektpartValue?.stodgrund?.varde?.toString();

    if (stodgrund) {
      const stodgrundParent = this.stodgrunder?.find((s) =>
        s.paragrafer.some((p) => p.stodgrundId.toString() === stodgrund),
      );
      if (stodgrundParent) {
        this.stodgrund.set(
          projektpart.worksite.id,
          stodgrundParent.stodgrundId.toString(),
        );
        this.sfsStodgrund.set(projektpart.worksite.id, stodgrund);
      } else {
        this.stodgrund.set(projektpart.worksite.id, stodgrund);
      }
    }

    if (this.isSharedView() || this.isApplicationCompletion()) {
      this.checkIfStodgrundOrMaxStodnivaHasChanged();
    }

    if (projektpartValue?.bidragPerAr?.some((bidrag) => bidrag.summa > 0)) {
      this.bidragsMottagare.add(projektpart.worksite.id);
    }

    /* CHANGES */
    this.setChanges(projektpart, projektpartValue);
    this.updateValidationForFinancer(projektpart.worksite.id);
  }

  private getProjektPartValue(projektpart: Projektpart): IProjektpart {
    const parsedValue = this.value.value
      ? JSON.parse(this.value.value)
      : undefined;
    return parsedValue?.projektparter?.find(
      (p) => p.id === projektpart.worksite.id,
    );
  }

  private checkCostChanges(
    effektaPartValue,
    parsedKostnaderValue,
    projektpartValue,
  ) {
    const parsedEffektakostnaderValue = this.getParsedEffektaValue(
      effektaPartValue,
      true,
    );
    if (projektpartValue) {
      if (
        this.isChangeRequestKomplettering() ||
        this.isApplicationCompletion()
      ) {
        const effektaChanges = this.getEffektaPartChanges(
          projektpartValue,
          true,
        );

        // För komplettering: lägg till beräknade total-rader i parsedKostnaderValue
        // så att merge har rätt värden att jämföra med
        const parsedKostnaderValueWithTotals = addRowAndColumnTotals(
          parsedKostnaderValue,
          this.yearColumns,
        );

        projektpartValue.kostnaderChanges = mergeEffektaAndUserChanges(
          effektaChanges,
          projektpartValue.kostnaderChanges ?? [],
          parsedKostnaderValueWithTotals,
          this.yearColumns,
        );
      } else if (this.isChangeRequest()) {
        projektpartValue.kostnaderChanges = [];
      } else {
        projektpartValue.kostnaderChanges = this.getEffektaPartChanges(
          projektpartValue,
          true,
        );
      }
      if (parsedEffektakostnaderValue && parsedKostnaderValue) {
        this.addChange(
          projektpartValue.kostnaderChanges,
          effektaPartValue,
          parsedEffektakostnaderValue,
          parsedKostnaderValue,
          projektpartValue.finansieringChanges,
          this.getEffektaPartChanges(projektpartValue, true),
        );
        const maxColumnId = Math.max(
          ...parsedEffektakostnaderValue.map((item) => Number(item.columnId)),
        );
        this.checkNewYearChanges(
          parsedKostnaderValue,
          projektpartValue.kostnaderChanges,
          maxColumnId,
          projektpartValue.finansieringChanges,
          effektaPartValue.id,
        );
      }
    }
  }

  private getEffektaPartChanges(projektpartValue, isCost = false) {
    // For change requests, use the effektaBudgetAnswer from begaran (set in constructor)
    // or fall back to budgetService.effektaBudgetAnswer
    let parsedValue;

    if (this.effektaBudgetAnswer) {
      // EffektaBudgetAnswer is already a parsed object with projektparter directly
      parsedValue = this.effektaBudgetAnswer;
    } else if (this.budgetService.effektaBudgetAnswer?.value) {
      // Answer has a value string that needs parsing
      try {
        parsedValue = JSON.parse(this.budgetService.effektaBudgetAnswer.value);
      } catch (err) {
        console.warn('Failed to parse budgetService.effektaBudgetAnswer:', err);
        return [];
      }
    } else {
      return [];
    }

    return getProjektpartChanges(parsedValue, projektpartValue?.id, isCost);
  }

  private checkForEgenFinansieringsChanges(
    id: string,
    columnId: number,
    finansieringChanges: ITableChange[],
  ) {
    const egenFin = this.egenFinansiering.get(id);
    const effektaEgenFinVal =
      this.effektaEgenFinansiering?.get(id)?.cellValues[columnId];
    if (egenFin) {
      for (const fin of egenFin) {
        if (
          +fin.columnId === columnId &&
          ((effektaEgenFinVal !== undefined &&
            fin.value !== effektaEgenFinVal) ||
            (fin.value !== 0 && effektaEgenFinVal === undefined)) &&
          !finansieringChanges.some(
            (c) =>
              c.rowId === '3' &&
              c.columnId === this.yearColumns[columnId]?.title &&
              (c.value === effektaEgenFinVal ||
                (c.value === 0 && !effektaEgenFinVal)),
          )
        ) {
          this.setChange(
            finansieringChanges,
            '3',
            this.yearColumns[columnId]?.title,
            effektaEgenFinVal ?? 0,
          );
        }
      }
    }
  }

  private checkFinancingChanges(
    effektaPartValue,
    parsedFinancingValue,
    projektpartValue,
  ) {
    const parsedEffektaFinansieringValue =
      this.getParsedEffektaValue(effektaPartValue);
    if (projektpartValue) {
      if (
        this.isChangeRequestKomplettering() ||
        this.isApplicationCompletion()
      ) {
        const effektaChanges = this.getEffektaPartChanges(projektpartValue);

        // För komplettering: lägg till NUVARANDE egen finansiering i parsedFinancingValue
        // så att merge har rätt värden att jämföra med
        const parsedFinancingValueWithEgenFin = parsedFinancingValue
          ? [...parsedFinancingValue]
          : [];

        // Använd nuvarande beräknade egenfinansiering från this.egenFinansiering
        const currentEgenFin = this.egenFinansiering.get(projektpartValue.id);
        if (currentEgenFin) {
          for (const egenFin of currentEgenFin) {
            parsedFinancingValueWithEgenFin.push({
              rowId: Finansieringstyp.EGENFINANSIERING,
              columnId: egenFin.columnId,
              value: egenFin.value,
            });
          }
        }

        // Lägg till kolumnsummor, radsummor och stortotal
        const parsedFinancingValueWithTotals = addRowAndColumnTotals(
          parsedFinancingValueWithEgenFin,
          this.yearColumns,
        );

        projektpartValue.finansieringChanges = mergeEffektaAndUserChanges(
          effektaChanges,
          projektpartValue.finansieringChanges ?? [],
          parsedFinancingValueWithTotals,
          this.yearColumns,
        );
      } else if (this.isChangeRequest()) {
        projektpartValue.finansieringChanges = [];
      } else {
        projektpartValue.finansieringChanges =
          this.getEffektaPartChanges(projektpartValue);
      }

      if (parsedEffektaFinansieringValue && parsedFinancingValue) {
        this.addChange(
          projektpartValue.finansieringChanges,
          effektaPartValue,
          parsedEffektaFinansieringValue,
          parsedFinancingValue,
          projektpartValue.finansieringChanges,
          this.getEffektaPartChanges(projektpartValue),
        );

        const maxColumnId = Math.max(
          ...parsedEffektaFinansieringValue.map((item) =>
            Number(item.columnId),
          ),
        );
        this.checkNewYearChanges(
          parsedFinancingValue,
          projektpartValue.finansieringChanges,
          maxColumnId,
          projektpartValue.finansieringChanges,
          effektaPartValue.id,
        );
        if (
          parsedFinancingValue?.length > parsedEffektaFinansieringValue?.length
        ) {
          const newFinansiar = parsedFinancingValue.filter(
            (f) =>
              !parsedEffektaFinansieringValue.some((i) => i.rowId === f.rowId),
          );
          this.checkNewFinancierChanges(
            newFinansiar,
            projektpartValue.finansieringChanges,
            effektaPartValue.id,
          );
        }
      }
    }
  }

  addChange(
    changes: ITableChange[],
    effektaPartValue,
    parsedEffektaValue,
    parsedValue,
    finansieringChanges: ITableChange[],
    effektaChanges: ITableChange[],
  ) {
    for (const oldValue of parsedEffektaValue) {
      for (const value of parsedValue) {
        const sameCell =
          oldValue.rowId === value.rowId &&
          oldValue.columnId === value.columnId;
        if (!sameCell) {
          continue;
        }

        const yearColumnId = this.yearColumns[oldValue.columnId]?.title;
        if (oldValue.value === value.value) {
          removeLocalChanges(changes, oldValue.rowId, yearColumnId);
          continue;
        }

        if (
          (this.isChangeRequest() &&
            !changes.some(
              (c) =>
                c.rowId === oldValue.rowId &&
                c.columnId === this.yearColumns[oldValue.columnId]?.title,
            )) ||
          this.isSharedView() ||
          this.isApplicationCompletion()
        ) {
          const comparisonChange = findLatestDecision(
            effektaChanges,
            oldValue.rowId,
            yearColumnId,
          );

          if (
            !comparisonChange ||
            value.value !== (comparisonChange.value ?? 0)
          ) {
            this.setChange(
              changes,
              oldValue.rowId,
              yearColumnId,
              oldValue.value,
            );
            this.checkForEgenFinansieringsChanges(
              effektaPartValue.id,
              +oldValue.columnId,
              finansieringChanges,
            );
          }
        }
      }
    }
  }

  private getParsedEffektaValue(effektaPartValue, isCost = false) {
    const effektaValue = effektaPartValue
      ? isCost
        ? this.getKostnadValueForWorksite(effektaPartValue)
        : this.getFinansieringValueForWorksite(effektaPartValue)
      : undefined;
    return effektaValue ? JSON.parse(effektaValue) : undefined;
  }

  private checkNewProjectPartChanges(
    parsedKostnaderValue,
    parsedFinancingValue,
    projektpartValue,
  ) {
    for (const kostnaderValue of parsedKostnaderValue) {
      if (
        kostnaderValue.value !== 0 &&
        !projektpartValue.kostnaderChanges.some(
          (c) =>
            c.rowId === kostnaderValue.rowId &&
            c.columnId === this.yearColumns[kostnaderValue.columnId]?.title,
        )
      ) {
        this.setChange(
          projektpartValue.kostnaderChanges,
          kostnaderValue.rowId,
          this.yearColumns[kostnaderValue.columnId]?.title,
          kostnaderValue.value,
          true,
        );

        this.checkForEgenFinansieringsChanges(
          projektpartValue.id,
          +kostnaderValue.columnId,
          projektpartValue.finansieringChanges,
        );
      }
    }

    for (const financingValue of parsedFinancingValue) {
      if (
        financingValue.value !== 0 &&
        !projektpartValue.finansieringChanges.some(
          (f) =>
            f.rowId === financingValue.rowId &&
            f.columnId === this.yearColumns[financingValue.columnId]?.title,
        )
      ) {
        this.setChange(
          projektpartValue.finansieringChanges,
          financingValue.rowId,
          this.yearColumns[financingValue.columnId]?.title,
          financingValue.value,
          true,
        );
        this.checkForEgenFinansieringsChanges(
          projektpartValue.id,
          +financingValue.columnId,
          projektpartValue.finansieringChanges,
        );
      }
    }
  }

  private checkNewYearChanges(
    parsedValue,
    projektpartValueChanges,
    maxColumnId,
    finansieringChanges,
    id,
  ) {
    for (const value of parsedValue) {
      if (
        value.columnId > maxColumnId &&
        value.value !== 0 &&
        this.yearColumns[value.columnId]?.title &&
        !projektpartValueChanges.some(
          (f) =>
            f.rowId === value.rowId &&
            f.columnId === this.yearColumns[value.columnId]?.title,
        )
      ) {
        this.setChange(
          projektpartValueChanges,
          value.rowId,
          this.yearColumns[value.columnId]?.title,
          value.value,
          true,
        );
        this.checkForEgenFinansieringsChanges(
          id,
          +value.columnId,
          finansieringChanges,
        );
      }
    }
  }

  private checkNewFinancierChanges(parsedValue, projektpartValueChanges, id) {
    for (const value of parsedValue) {
      if (
        value.value !== 0 &&
        !projektpartValueChanges.some(
          (f) =>
            f.rowId === value.rowId &&
            f.columnId === this.yearColumns[value.columnId]?.title,
        )
      ) {
        this.setChange(
          projektpartValueChanges,
          value.rowId,
          this.yearColumns[value.columnId]?.title,
          value.value,
          true,
        );

        const meta = this.getChangeTooltipMeta();
        if (!meta) {
          continue;
        }

        projektpartValueChanges.push({
          rowId: '3',
          columnId: this.yearColumns[value.columnId]?.title,
          value:
            this.effektaEgenFinansiering?.get(id)?.cellValues[+value.columnId],
          text: meta.text,
          tooltipTextKey: meta.tooltipTextKey,
          date: this.senastBeslut ?? new Date(),
        });
      }
    }
  }

  private setChange(
    changes: ITableChange[],
    rowId: string,
    columnId: string,
    value: number,
    newYearOrFinancier = false,
  ) {
    if (
      changes.some(
        (change) =>
          change.rowId === rowId &&
          change.columnId === columnId &&
          change.order === undefined,
      )
    ) {
      return;
    }

    const meta = this.getChangeTooltipMeta();
    if (!meta) {
      return;
    }

    changes.push({
      rowId,
      columnId,
      value: newYearOrFinancier ? 0 : value,
      text: meta.text,
      tooltipTextKey: meta.tooltipTextKey,
      date: this.senastBeslut ?? new Date(),
    });
  }

  private setChanges(
    projektpart: Projektpart,
    projektpartValue?: IProjektpart,
  ) {
    const finansieringRow = this.finansieringRows.get(projektpart.worksite.id);

    if (this.isApplicationCompletion()) {
      this.setEgenFinansiering(projektpart.worksite.id);
    }

    this.hasCostChanges.set(projektpart.worksite.id, false);
    const kostnaderChanges: BudgetTableChanges = [];
    const projektpartKostnader = this.projektpartKostnader.get(
      projektpart.worksite.id,
    );

    for (const [rowIndex, row] of this.KOSTNADSSLAG.entries()) {
      const rowId = row.id;
      kostnaderChanges[rowIndex] = [];
      for (const [colIndex, col] of this.yearColumns.entries()) {
        const colId = col.title;
        const changes = projektpartValue?.kostnaderChanges?.filter(
          (c) =>
            c.rowId?.toString() === rowId && c.columnId?.toString() === colId,
        );
        kostnaderChanges[rowIndex][colIndex] = changes;
        if (
          (changes?.length > 0 &&
            (this.isSharedView() || this.isApplicationCompletion())) ||
          this.isChangeRequest()
        ) {
          this.hasCostChanges.set(projektpart.worksite.id, true);
        }

        // Direkta jämförelser körs INTE under komplettering (skapar falska markeringar)
        if (
          (changes?.length > 0 && this.isSharedView()) ||
          (this.isChangeRequest() && !this.isChangeRequestKomplettering()) ||
          this.isApplicationCompletion()
        ) {
          const totalKostnader = this.totalKostnader.find(
            (kostnad) =>
              +kostnad.columnId === colIndex && kostnad.rowId === rowId,
          );

          const totalKostnaderFromEffekta = this.totalKostnaderFromEffekta.find(
            (kostnad) =>
              +kostnad.columnId === colIndex && kostnad.rowId === rowId,
          );
          if (
            (totalKostnaderFromEffekta?.value !== undefined &&
              totalKostnader.value !== totalKostnaderFromEffekta.value) ||
            (this.isNewYear(colIndex) && totalKostnader?.value)
          ) {
            this.totalKostnaderChanges[rowIndex][colIndex] =
              this.setChangeObject(
                this.isSharedView()
                  ? rowIndex.toString()
                  : totalKostnader?.rowId,
                colIndex.toString(),
                totalKostnaderFromEffekta?.value,
              );
          }

          if (!kostnaderChanges[this.KOSTNADSSLAG.length]) {
            kostnaderChanges[this.KOSTNADSSLAG.length] = [];
          }

          if (
            (this.effektaKostnader.get(projektpart.worksite.id) ||
              (projektpartValue?.isNew &&
                projektpartKostnader?.rows[rowIndex] !== 0)) &&
            projektpartKostnader?.rows[rowIndex] !==
              this.effektaKostnader.get(projektpart.worksite.id)?.rows[rowIndex]
          ) {
            kostnaderChanges[rowIndex][this.yearColumns.length] =
              this.setChangeObject(
                rowIndex,
                this.yearColumns.length,
                this.effektaKostnader.get(projektpart.worksite.id)?.rows[
                  rowIndex
                ],
              );
          }

          if (
            ((this.effektaKostnader.get(projektpart.worksite.id) ||
              (projektpartValue?.isNew &&
                projektpartKostnader?.columns[colIndex] !== 0)) &&
              projektpartKostnader?.columns[colIndex] !==
                this.effektaKostnader.get(projektpart.worksite.id)?.columns[
                  colIndex
                ] &&
              !this.isNewYear(colIndex)) ||
            (this.isNewYear(colIndex) &&
              projektpartKostnader?.columns[colIndex] !== 0)
          ) {
            kostnaderChanges[this.KOSTNADSSLAG.length][colIndex] =
              this.setChangeObject(
                this.KOSTNADSSLAG.length,
                colIndex,
                this.effektaKostnader.get(projektpart.worksite.id)?.columns[
                  colIndex
                ],
              );
          }
          if (
            (this.effektaKostnader.get(projektpart.worksite.id) ||
              (projektpartValue?.isNew &&
                projektpartKostnader?.totalSum !== 0)) &&
            projektpartKostnader?.totalSum !==
              this.effektaKostnader.get(projektpart.worksite.id)?.totalSum
          ) {
            kostnaderChanges[this.KOSTNADSSLAG.length][
              this.yearColumns.length
            ] = this.setChangeObject(
              this.KOSTNADSSLAG.length,
              this.yearColumns.length,
              this.effektaKostnader.get(projektpart.worksite.id)?.totalSum,
            );
          }
          if (
            this.totalEffektaKostnader?.rows?.[rowIndex] != undefined &&
            this.totalKostnaderSummor?.rows?.[rowIndex] !==
              this.totalEffektaKostnader?.rows?.[rowIndex]
          ) {
            this.totalKostnaderChanges[rowIndex][this.yearColumns.length] =
              this.setChangeObject(
                rowIndex.toString(),
                this.yearColumns.length.toString(),
                this.totalEffektaKostnader.rows[rowIndex],
              );
          }

          if (
            (this.totalEffektaKostnader?.columns?.[colIndex] !== undefined &&
              this.totalKostnaderSummor?.columns?.[colIndex] !==
                this.totalEffektaKostnader?.columns?.[colIndex]) ||
            (this.isNewYear(colIndex) &&
              this.totalKostnaderSummor?.columns?.[colIndex] !== 0)
          ) {
            this.totalKostnaderChanges[this.KOSTNADSSLAG.length][colIndex] =
              this.setChangeObject(
                this.KOSTNADSSLAG.length.toString(),
                colIndex.toString(),
                this.isNewYear(colIndex)
                  ? 0
                  : this.totalEffektaKostnader?.columns?.[colIndex],
              );
          }
          if (
            this.totalEffektaKostnader?.totalSum !== undefined &&
            this.totalKostnaderSummor.totalSum !==
              this.totalEffektaKostnader?.totalSum
          ) {
            this.totalKostnaderChanges[this.KOSTNADSSLAG.length][
              this.yearColumns.length
            ] = this.setChangeObject(
              this.KOSTNADSSLAG.length.toString(),
              this.yearColumns.length.toString(),
              this.totalEffektaKostnader.totalSum,
            );
          }
        }
      }
      if (this.shouldApplyIncomingBudgetChanges()) {
        const summaKostnaderChanges =
          projektpartValue?.kostnaderChanges?.filter(
            (c) =>
              c.rowId?.toString() === rowId &&
              c.columnId?.toString() === 'total',
          );
        if (summaKostnaderChanges?.length > 0) {
          kostnaderChanges[rowIndex][this.yearColumns.length] =
            summaKostnaderChanges;
        }
      }
    }
    if (this.shouldApplyIncomingBudgetChanges()) {
      if (kostnaderChanges[this.KOSTNADSSLAG.length] === undefined) {
        kostnaderChanges[this.KOSTNADSSLAG.length] = [];
      }
      for (const [colIndex, col] of this.yearColumns.entries()) {
        const totalKostnaderChanges =
          projektpartValue?.kostnaderChanges?.filter(
            (c) =>
              c.rowId?.toString() === 'total' &&
              c.columnId?.toString() === col.title,
          );
        if (totalKostnaderChanges?.length > 0) {
          kostnaderChanges[this.KOSTNADSSLAG.length][colIndex] =
            totalKostnaderChanges;
        }
      }
      const totalSummaKostnaderChanges =
        projektpartValue?.kostnaderChanges?.filter(
          (c) =>
            c.rowId?.toString() === 'total' &&
            c.columnId?.toString() === 'total',
        );
      if (totalSummaKostnaderChanges?.length > 0) {
        kostnaderChanges[this.KOSTNADSSLAG.length][this.yearColumns.length] =
          totalSummaKostnaderChanges;
      }
    }

    this.hasFinansieringChanges.set(projektpart.worksite.id, false);
    const finansieringChanges: BudgetTableChanges = [];
    for (const [rowIndex, row] of finansieringRow.entries()) {
      const rowId = row.id;
      finansieringChanges[rowIndex] = [];
      for (const [colIndex, col] of this.yearColumns.entries()) {
        const colId = col.title;
        const changes = projektpartValue?.finansieringChanges?.filter(
          (c) =>
            c.rowId.toString() === rowId && c.columnId.toString() === colId,
        );

        finansieringChanges[rowIndex][colIndex] = changes;
        if (
          (changes?.length > 0 &&
            (this.isSharedView() || this.isApplicationCompletion())) ||
          this.isChangeRequest()
        ) {
          this.hasFinansieringChanges.set(projektpart.worksite.id, true);
        }

        // Direkta jämförelser körs INTE under komplettering (skapar falska markeringar)
        if (
          (changes?.length > 0 && this.isSharedView()) ||
          (this.isChangeRequest() && !this.isChangeRequestKomplettering()) ||
          this.isApplicationCompletion()
        ) {
          const projektpartFinansiering = this.projektpartFinansiering.get(
            projektpart.worksite.id,
          );
          const totalFinansiering = this.totalFinansiering.find(
            (f) => +f.columnId === colIndex && f.rowId === rowId,
          );
          const totalFinansieringFromEffekta =
            this.totalFinansieringFromEffekta.find(
              (f) => +f.columnId === colIndex && f.rowId === rowId,
            );
          if (
            totalFinansieringFromEffekta?.value !== undefined &&
            totalFinansiering.value !== totalFinansieringFromEffekta.value &&
            rowIndex === 0
          ) {
            this.totalFinansieringChanges[rowIndex][colIndex] =
              this.setChangeObject(
                rowIndex.toString(),
                colIndex.toString(),
                totalFinansieringFromEffekta?.value,
              );
          }
          if (
            this.getNewFinancierChange(finansieringChanges, rowIndex, colIndex)
          ) {
            if (
              this.totalColumnsAnnanFinansiering[colIndex] !==
              this.effektaTotalColumnsOtherFinansiering[colIndex]
            ) {
              this.totalFinansieringChanges[1][colIndex] = this.setChangeObject(
                Finansieringstyp.ANNANFINANSIERING,
                colIndex.toString(),
                this.effektaTotalColumnsOtherFinansiering[colIndex],
              );
            }
            if (
              this.totalOtherFinansiering !== this.totalOtherEffektaFinansiering
            ) {
              this.totalFinansieringChanges[1][this.yearColumns.length] =
                this.setChangeObject(
                  Finansieringstyp.ANNANFINANSIERING,
                  this.yearColumns.length.toString(),
                  this.totalOtherEffektaFinansiering,
                );
            }
          }

          if (!finansieringChanges[finansieringRow.length]) {
            finansieringChanges[finansieringRow.length] = [];
          }

          if (
            this.effektaFinansiering.get(projektpart.worksite.id) ||
            projektpartValue?.isNew
          ) {
            if (
              row.id === Finansieringstyp.VINNOVABIDRAG &&
              ((projektpartFinansiering?.rows[rowIndex] !== undefined &&
                projektpartFinansiering?.rows[rowIndex] !==
                  this.effektaFinansiering.get(projektpart.worksite.id)
                    ?.totalSum &&
                !projektpartValue?.isNew) ||
                (projektpartValue?.isNew &&
                  projektpartFinansiering?.rows[rowIndex] !== 0))
            ) {
              finansieringChanges[rowIndex][this.yearColumns.length] =
                this.setChangeObject(
                  row.id,
                  this.yearColumns.length.toString(),
                  this.effektaFinansiering.get(projektpart.worksite.id)
                    ?.totalSum,
                );
            } else {
              if (
                row.id === Finansieringstyp.EGENFINANSIERING &&
                ((this.effektaEgenFinansiering?.get(projektpart.worksite.id)
                  ?.totalRowSum !== undefined &&
                  this.effektaEgenFinansiering?.get(projektpart.worksite.id)
                    ?.totalRowSum !==
                    this.projektpartEgenFinansiering.get(
                      projektpart.worksite.id,
                    )?.totalRowSum) ||
                  (this.effektaEgenFinansiering?.get(projektpart.worksite.id)
                    ?.totalRowSum === undefined &&
                    this.projektpartEgenFinansiering.get(
                      projektpart.worksite.id,
                    )?.totalRowSum) ||
                  (projektpartValue?.isNew &&
                    this.projektpartEgenFinansiering.get(
                      projektpart.worksite.id,
                    )?.totalRowSum !== 0))
              ) {
                finansieringChanges[rowIndex][this.yearColumns.length] =
                  this.setChangeObject(
                    rowIndex.toString(),
                    this.yearColumns.length.toString(),
                    this.effektaEgenFinansiering?.get(projektpart.worksite.id)
                      ?.totalRowSum,
                  );
              }
              const newFinancierChange = this.getNewFinancierChange(
                finansieringChanges,
                rowIndex,
                colIndex,
              );
              if (newFinancierChange) {
                const finansiarId = newFinancierChange.rowId;
                finansieringChanges[rowIndex][this.yearColumns.length] =
                  this.setChangeObject(
                    rowIndex.toString(),
                    this.yearColumns.length.toString(),
                    this.getEffektaFinansieringSumma(
                      projektpart.worksite.id,
                      finansiarId,
                    ),
                  );
              }
            }
          }

          if (
            (this.effektaKostnader.get(projektpart.worksite.id) ||
              projektpartValue?.isNew) &&
            this.hasCostChanges.get(projektpart.worksite.id)
          ) {
            if (
              (projektpartKostnader?.columns[colIndex] !==
                this.effektaKostnader.get(projektpart.worksite.id)?.columns[
                  colIndex
                ] &&
                !projektpartValue?.isNew) ||
              (projektpartValue?.isNew &&
                projektpartKostnader?.columns[colIndex] !== 0)
            ) {
              finansieringChanges[finansieringRow.length][colIndex] =
                this.setChangeObject(
                  finansieringRow.length.toString(),
                  colIndex.toString(),
                  this.effektaKostnader.get(projektpart.worksite.id)?.columns[
                    colIndex
                  ],
                );
            }
            if (
              (projektpartKostnader?.totalSum !==
                this.effektaKostnader.get(projektpart.worksite.id)?.totalSum &&
                !projektpartValue?.isNew) ||
              (projektpartValue?.isNew && projektpartKostnader?.totalSum !== 0)
            ) {
              finansieringChanges[finansieringRow.length][
                this.yearColumns.length
              ] = this.setChangeObject(
                finansieringRow.length.toString(),
                this.yearColumns.length.toString(),
                this.effektaKostnader.get(projektpart.worksite.id)?.totalSum,
              );
            }
          }
          if (
            rowIndex === 0 &&
            this.totalEffektaFinansiering?.rows?.[rowIndex] !== undefined &&
            this.totalFinansieringsSummor?.rows?.[rowIndex] !==
              this.totalEffektaFinansiering?.rows?.[rowIndex]
          ) {
            this.totalFinansieringChanges[rowIndex][this.yearColumns.length] =
              this.setChangeObject(
                rowIndex.toString(),
                this.yearColumns.length.toString(),
                this.totalEffektaFinansiering?.rows?.[rowIndex],
              );
          }
          if (
            this.totalEgenFinansiering !== this.effektaTotalEgenFinansiering
          ) {
            this.totalFinansieringChanges[this.FINANSIERING.length - 1][
              this.yearColumns.length
            ] = this.setChangeObject(
              (this.FINANSIERING.length - 1).toString(),
              this.yearColumns.length.toString(),
              this.effektaTotalEgenFinansiering,
            );
          }
          const newYear = this.isNewYear(colIndex);
          if (
            (this.hasFinansieringChanges.get(projektpart.worksite.id) &&
              this.totalEffektaKostnader?.columns?.[colIndex] &&
              !newYear) ||
            [...this.hasCostChanges.values()].some(Boolean)
          ) {
            if (
              (!newYear &&
                this.totalColumnsEgenFinansiering[colIndex] !==
                  this.effektaTotalColumnsEgenFinansiering[colIndex]) ||
              (newYear && this.totalColumnsEgenFinansiering[colIndex] !== 0)
            ) {
              this.totalFinansieringChanges[this.FINANSIERING.length - 1][
                colIndex
              ] = this.setChangeObject(
                (this.FINANSIERING.length - 1).toString(),
                colIndex.toString(),
                this.effektaTotalColumnsEgenFinansiering[colIndex],
              );
            }
            if (
              (this.totalEffektaKostnader?.columns?.[colIndex] !== undefined &&
                this.totalEffektaKostnader?.columns?.[colIndex] !==
                  this.totalKostnaderSummor?.columns?.[colIndex]) ||
              (this.totalEffektaKostnader?.columns?.[colIndex] === undefined &&
                this.totalKostnaderSummor?.columns?.[colIndex])
            ) {
              this.totalFinansieringChanges[this.FINANSIERING.length][
                colIndex
              ] = this.setChangeObject(
                this.FINANSIERING.length.toString(),
                colIndex.toString(),
                this.totalEffektaKostnader?.columns?.[colIndex],
              );
            }
            if (
              this.totalEffektaKostnader?.totalSum !==
              this.totalFinansieringsSumma
            ) {
              this.totalFinansieringChanges[this.FINANSIERING.length][
                this.yearColumns.length
              ] = this.setChangeObject(
                this.FINANSIERING.length.toString(),
                this.yearColumns.length.toString(),
                this.totalEffektaKostnader?.totalSum,
              );
            }
          }
        }
      }
      if (this.shouldApplyIncomingBudgetChanges()) {
        const summaFinansieringChanges =
          projektpartValue?.finansieringChanges?.filter(
            (c) =>
              c.rowId?.toString() === rowId &&
              c.columnId?.toString() === 'total',
          );
        if (summaFinansieringChanges?.length > 0) {
          finansieringChanges[rowIndex][this.yearColumns.length] =
            summaFinansieringChanges;
        }
      }
    }

    if (this.shouldApplyIncomingBudgetChanges()) {
      if (
        finansieringChanges[projektpart.financiers.length + 2] === undefined
      ) {
        finansieringChanges[projektpart.financiers.length + 2] = [];
      }
      for (const [colIndex, col] of this.yearColumns.entries()) {
        const totalFinansieringChanges =
          projektpartValue?.finansieringChanges?.filter(
            (c) =>
              c.rowId?.toString() === 'total' &&
              c.columnId?.toString() === col.title,
          );
        if (totalFinansieringChanges?.length > 0) {
          finansieringChanges[projektpart.financiers.length + 2][colIndex] =
            totalFinansieringChanges;
        }
      }
      const totalSummaFinansieringChanges =
        projektpartValue?.finansieringChanges?.filter(
          (c) =>
            c.rowId?.toString() === 'total' &&
            c.columnId?.toString() === 'total',
        );
      if (totalSummaFinansieringChanges?.length > 0) {
        finansieringChanges[projektpart.financiers.length + 2][
          this.yearColumns.length
        ] = totalSummaFinansieringChanges;
      }
    }

    if (projektpart.financiers) {
      for (const [index, financier] of projektpart.financiers.entries()) {
        const effektaPartValue = this.effektaBudgetAnswer?.projektparter?.find(
          (p) => p.id === projektpart.worksite.id,
        );

        const financiersChanges =
          (effektaPartValue &&
            (effektaPartValue?.finansiarer?.length === 0 ||
              !effektaPartValue?.finansiarer?.some(
                (f) => f.id === financier.id,
              ))) ||
          !effektaPartValue
            ? true
            : false;

        // Inkommande markeringar kan finnas även om finansiären redan finns.
        const hasFinancierChangesInData =
          this.isChangeRequestKomplettering() || this.isApplicationCompletion()
            ? projektpartValue?.finansieringChanges?.some(
                (c) => c.rowId === financier.id,
              )
            : false;

        if (
          !financier.isNew &&
          !financiersChanges &&
          !hasFinancierChangesInData
        ) {
          continue;
        }
        this.hasFinansieringChanges.set(projektpart.worksite.id, true);
        finansieringChanges[index + 1]['head'] = [
          {
            new: true,
          },
        ];
        this.totalFinansieringChanges[1]['head'] = [
          {
            new: true,
          },
        ];
      }
    }

    if (this.isApplicationCompletion()) {
      removeRestoredTableChanges(
        kostnaderChanges,
        this.getEffektaPartChanges(projektpartValue, true),
        addRowAndColumnTotals(
          this.getKostnaderTableValues(projektpart.worksite.id),
          this.yearColumns,
        ),
        this.yearColumns,
        this.KOSTNADSSLAG.map((row) => row.id),
      );
      removeRestoredTableChanges(
        finansieringChanges,
        this.getEffektaPartChanges(projektpartValue),
        addRowAndColumnTotals(
          [
            ...this.getFinansieringTableValues(projektpart.worksite.id),
            ...(this.egenFinansiering.get(projektpart.worksite.id) ?? []),
          ],
          this.yearColumns,
        ),
        this.yearColumns,
        finansieringRow.map((row) => row.id),
      );
    }

    this.setTableHeadersChanges([
      kostnaderChanges,
      finansieringChanges,
      this.totalKostnaderChanges,
      this.totalFinansieringChanges,
    ]);

    this.rendererFormRepository.setShowChangesAnswers(
      'budget',
      [...this.hasCostChanges.values()].some(Boolean) ||
        [...this.hasFinansieringChanges.values()].some(Boolean) ||
        this.newYearColumnCount > 0,
    );
    this.kostnaderChanges.set(projektpart.worksite.id, kostnaderChanges);
    this.finansieringChanges.set(projektpart.worksite.id, finansieringChanges);

    if (
      this.isSharedView() ||
      this.isChangeRequest() ||
      this.isApplicationCompletion()
    ) {
      this.setChangesForTotalTable();
    }
  }

  private isNewYear(colIndex: number): boolean {
    return (
      this.newProjektEndDate?.getFullYear() !== undefined &&
      +this.yearColumns[colIndex]?.title ===
        this.newProjektEndDate.getFullYear()
    );
  }

  private isGuid(value: string): boolean {
    const guidRegex =
      /^[\dA-Fa-f]{8}-[\dA-Fa-f]{4}-[1-8][\dA-Fa-f]{3}-[89ABab][\dA-Fa-f]{3}-[\dA-Fa-f]{12}$/;
    return guidRegex.test(value);
  }

  private getNewFinancierChange(
    changes: BudgetTableChanges,
    rowIndex: number,
    colIndex: number,
  ): ITableChange | undefined {
    return changes?.[rowIndex]?.[colIndex]?.find((change) =>
      this.isGuid(change.rowId?.toString()),
    );
  }

  private setChangesForTotalTable() {
    const incomingKostnaderTotalChanges =
      this.effektaBudgetAnswer?.kostnaderTotalChanges ?? [];
    const incomingFinansieringTotalChanges =
      this.effektaBudgetAnswer?.finansieringTotalChanges ?? [];
    let kostnaderTotalChanges = incomingKostnaderTotalChanges;
    let finansieringTotalChanges = incomingFinansieringTotalChanges;
    const parsedKostnaderCurrentValues: TableRowValueList = [];
    const parsedFinansieringCurrentValues: TableRowValueList = [];

    if (this.isChangeRequestKomplettering() || this.isApplicationCompletion()) {
      for (const kostnad of this.totalKostnader) {
        parsedKostnaderCurrentValues.push({
          rowId: kostnad.rowId,
          columnId: kostnad.columnId,
          value: kostnad.value,
        });
      }

      if (this.totalKostnaderSummor) {
        for (let colIndex = 0; colIndex < this.yearColumns.length; colIndex++) {
          parsedKostnaderCurrentValues.push({
            rowId: 'total',
            columnId: colIndex.toString(),
            value: this.totalKostnaderSummor.columns[colIndex] || 0,
          });
        }

        for (
          let rowIndex = 0;
          rowIndex < this.KOSTNADSSLAG.length;
          rowIndex++
        ) {
          parsedKostnaderCurrentValues.push({
            rowId: (rowIndex + 1).toString(),
            columnId: 'total',
            value: this.totalKostnaderSummor.rows[rowIndex] || 0,
          });
        }

        parsedKostnaderCurrentValues.push({
          rowId: 'total',
          columnId: 'total',
          value: this.totalKostnaderSummor.totalSum || 0,
        });
      }

      if (kostnaderTotalChanges && kostnaderTotalChanges.length > 0) {
        kostnaderTotalChanges = mergeEffektaAndUserChanges(
          kostnaderTotalChanges,
          [],
          parsedKostnaderCurrentValues,
          this.yearColumns,
        );
      }

      for (const fin of this.totalFinansiering) {
        parsedFinansieringCurrentValues.push({
          rowId: fin.rowId,
          columnId: fin.columnId,
          value: fin.value,
        });
      }

      if (this.totalFinansieringsSummor) {
        for (let colIndex = 0; colIndex < this.yearColumns.length; colIndex++) {
          parsedFinansieringCurrentValues.push({
            rowId: 'total',
            columnId: colIndex.toString(),
            value: this.totalFinansieringsSummor.columns[colIndex] || 0,
          });
        }

        for (
          let rowIndex = 0;
          rowIndex < this.FINANSIERING.length;
          rowIndex++
        ) {
          parsedFinansieringCurrentValues.push({
            rowId: (rowIndex + 1).toString(),
            columnId: 'total',
            value: this.totalFinansieringsSummor.rows[rowIndex] || 0,
          });
        }

        parsedFinansieringCurrentValues.push({
          rowId: 'total',
          columnId: 'total',
          value: this.totalFinansieringsSummor.totalSum || 0,
        });
      }

      if (finansieringTotalChanges && finansieringTotalChanges.length > 0) {
        finansieringTotalChanges = mergeEffektaAndUserChanges(
          finansieringTotalChanges,
          [],
          parsedFinansieringCurrentValues,
          this.yearColumns,
        );
      }
    }

    if (this.shouldApplyIncomingBudgetChanges()) {
      for (const [rowIndex, row] of this.KOSTNADSSLAG.entries()) {
        const rowId = row.id;
        for (const [colIndex, col] of this.yearColumns.entries()) {
          const colId = col.title;
          const changes = kostnaderTotalChanges?.filter(
            (c) =>
              c.rowId?.toString() === rowId && c.columnId?.toString() === colId,
          );
          if (this.totalKostnaderChanges[rowIndex] === undefined) {
            this.totalKostnaderChanges[rowIndex] = [];
          }
          if (changes && changes.length > 0) {
            this.totalKostnaderChanges[rowIndex][colIndex] =
              mergeIncomingChanges(
                changes,
                this.totalKostnaderChanges[rowIndex][colIndex],
              );
          }

          const summaKostnaderChanges = kostnaderTotalChanges?.filter(
            (c) =>
              c.rowId?.toString() === rowId &&
              c.columnId?.toString() === 'total',
          );
          if (summaKostnaderChanges?.length > 0) {
            this.totalKostnaderChanges[rowIndex][this.yearColumns.length] =
              mergeIncomingChanges(
                summaKostnaderChanges,
                this.totalKostnaderChanges[rowIndex][this.yearColumns.length],
              );
          }
        }
      }
      if (this.totalKostnaderChanges[this.KOSTNADSSLAG.length] === undefined) {
        this.totalKostnaderChanges[this.KOSTNADSSLAG.length] = [];
      }
      for (const [colIndex, col] of this.yearColumns.entries()) {
        const totalKostnaderChanges = kostnaderTotalChanges?.filter(
          (c) =>
            c.rowId?.toString() === 'total' &&
            c.columnId?.toString() === col.title,
        );
        if (totalKostnaderChanges?.length > 0) {
          this.totalKostnaderChanges[this.KOSTNADSSLAG.length][colIndex] =
            mergeIncomingChanges(
              totalKostnaderChanges,
              this.totalKostnaderChanges[this.KOSTNADSSLAG.length][colIndex],
            );
        }
      }
      const totalSummaKostnaderChanges = kostnaderTotalChanges?.filter(
        (c) =>
          c.rowId?.toString() === 'total' && c.columnId?.toString() === 'total',
      );
      if (totalSummaKostnaderChanges?.length > 0) {
        this.totalKostnaderChanges[this.KOSTNADSSLAG.length][
          this.yearColumns.length
        ] = mergeIncomingChanges(
          totalSummaKostnaderChanges,
          this.totalKostnaderChanges[this.KOSTNADSSLAG.length][
            this.yearColumns.length
          ],
        );
      }

      if (
        this.totalFinansieringChanges[this.FINANSIERING.length] === undefined
      ) {
        this.totalFinansieringChanges[this.FINANSIERING.length] = [];
      }
      for (const [rowIndex, row] of this.FINANSIERING.entries()) {
        const rowId = row.id;
        for (const [colIndex, col] of this.yearColumns.entries()) {
          const colId = col.title;
          const changes = finansieringTotalChanges?.filter(
            (c) =>
              c.rowId.toString() === rowId && c.columnId.toString() === colId,
          );
          if (this.totalFinansieringChanges[rowIndex] === undefined) {
            this.totalFinansieringChanges[rowIndex] = [];
          }
          if (changes && changes.length > 0) {
            this.totalFinansieringChanges[rowIndex][colIndex] =
              mergeIncomingChanges(
                changes,
                this.totalFinansieringChanges[rowIndex][colIndex],
              );
          }
        }

        const summaFinansieringChanges = finansieringTotalChanges?.filter(
          (c) =>
            c.rowId?.toString() === rowId && c.columnId?.toString() === 'total',
        );
        if (summaFinansieringChanges?.length > 0) {
          this.totalFinansieringChanges[rowIndex][this.yearColumns.length] =
            mergeIncomingChanges(
              summaFinansieringChanges,
              this.totalFinansieringChanges[rowIndex][this.yearColumns.length],
            );
        }
      }

      for (const [colIndex, col] of this.yearColumns.entries()) {
        const totalFinansieringChanges = finansieringTotalChanges?.filter(
          (c) =>
            c.rowId?.toString() === 'total' &&
            c.columnId?.toString() === col.title,
        );
        if (totalFinansieringChanges?.length > 0) {
          this.totalFinansieringChanges[this.FINANSIERING.length][colIndex] =
            mergeIncomingChanges(
              totalFinansieringChanges,
              this.totalFinansieringChanges[this.FINANSIERING.length][colIndex],
            );
        }
      }
      const totalSummaFinansieringChanges = finansieringTotalChanges?.filter(
        (c) =>
          c.rowId?.toString() === 'total' && c.columnId?.toString() === 'total',
      );
      if (totalSummaFinansieringChanges?.length > 0) {
        this.totalFinansieringChanges[this.FINANSIERING.length][
          this.yearColumns.length
        ] = mergeIncomingChanges(
          totalSummaFinansieringChanges,
          this.totalFinansieringChanges[this.FINANSIERING.length][
            this.yearColumns.length
          ],
        );
      }
    }

    if (this.isApplicationCompletion()) {
      removeRestoredTableChanges(
        this.totalKostnaderChanges,
        incomingKostnaderTotalChanges,
        parsedKostnaderCurrentValues,
        this.yearColumns,
        this.KOSTNADSSLAG.map((row) => row.id),
      );
      removeRestoredTableChanges(
        this.totalFinansieringChanges,
        incomingFinansieringTotalChanges,
        parsedFinansieringCurrentValues,
        this.yearColumns,
        this.FINANSIERING.map((row) => row.id),
      );
    }
  }

  private setChangeObject(rowId, columnId, value): ITableChange[] {
    return [
      {
        rowId: rowId,
        columnId: columnId,
        value: value ?? 0,
        text: this.senastBeslut
          ? this.translocoService.translate('begaran_budget_tooltip')
          : this.translocoService.translate('ansokan_budget_tooltip'),
        tooltipTextKey: this.senastBeslut
          ? 'begaran_budget_tooltip'
          : 'ansokan_budget_tooltip',
        date: this.senastBeslut ?? new Date(),
        new: true,
      },
    ];
  }
  private setYearColumns(value): void {
    this.startDate = this.isChangeRequest()
      ? new Date(this.projektStartDate)
      : value?.fromDate
        ? new Date(value.fromDate)
        : undefined;
    this.endDate = this.isChangeRequest()
      ? new Date(this.projektEndDate)
      : value?.toDate
        ? new Date(value.toDate)
        : undefined;
    const startYear = this.startDate?.getFullYear();
    const endYear = this.newProjektEndDate
      ? this.newProjektEndDate?.getFullYear()
      : this.endDate?.getFullYear();

    const {
      browseSmall: browseSmall,
      browseMedium,
      browseLarge,
      yearColumns,
    } = getBudgetTableColumns(startYear, endYear);

    this.yearColumns = yearColumns;
    this.browseLarge.set(browseLarge);
    this.browseMedium.set(browseMedium);
    this.browseSmall.set(browseSmall);

    this.cdr.markForCheck();
  }

  toggleCollapseAll(open: boolean, inactive: boolean): void {
    for (const isOpenItem of this.isOpen) {
      if (
        this.parter.koordinator.worksite.id === isOpenItem[0] ||
        (!inactive &&
          this.inaktivaProjektparter.some(
            (pp) => pp.worksite.id === isOpenItem[0],
          )) ||
        (inactive &&
          !this.inaktivaProjektparter.some(
            (pp) => pp.worksite.id === isOpenItem[0],
          ))
      ) {
        continue;
      }
      this.isOpen.set(isOpenItem[0], open);
    }
  }

  private updateBidragsMottagareNotFinansiarForAllControls(): void {
    for (const [
      worksiteId,
      control,
    ] of this.finansieringFormControl.entries()) {
      const rowValidations =
        this.finansieringRowValidation.get(worksiteId) || [];
      const mottagareNotFinansiarValidations = rowValidations.filter(
        (validation) =>
          validation.validation === BudgetValidation.MottagareNotFinansiar,
      );
      this.setValidation(control, [], mottagareNotFinansiarValidations, false);
      control.updateValueAndValidity();

      if (this.hasBidragsMottagareIsFinansiarError(worksiteId)) {
        control.markAsTouched();
      }
      this.updateValidation$.next(true);
    }
  }

  private updateValidationForFinancer(worksiteId: string): void {
    const control = this.finansieringFormControl.get(worksiteId);
    const projektpartControl = this.kostnaderFormControl.get(worksiteId);
    if (!control) {
      console.warn(`No control found for worksiteId: ${worksiteId}`);
      return;
    }

    const controlValue = projektpartControl.value;

    const parsedValue = controlValue ? JSON.parse(controlValue) : [];

    const allValuesAreZero = parsedValue.every((item) => item.value === 0);

    const rowValidations = this.finansieringRowValidation.get(worksiteId) || [];
    const financierValidations = rowValidations.filter(
      (validation) =>
        validation.validation === BudgetValidation.FinansiarRequired ||
        validation.validation === BudgetValidation.MottagareNotFinansiar ||
        validation.validation === BudgetValidation.Under10KWarning ||
        validation.validation === BudgetValidation.EgenFinansieringRequired,
    );

    if (allValuesAreZero) {
      return;
    }

    this.setValidation(control, [], financierValidations, false);

    control.updateValueAndValidity();
    control.markAsTouched();
  }

  private hasBidragsMottagareIsFinansiarError(worksiteId: string): boolean {
    const control = this.finansieringFormControl.get(worksiteId);
    if (!control || !control.errors) {
      return false;
    }
    return Object.keys(control.errors).some((key) =>
      key.startsWith('BudgetMottagareNotFinansiar'),
    );
  }

  updateBudgetTables(save: boolean, worksiteId: string): void {
    if (!save) {
      return;
    }
    const inactiveWorksite = this.inaktivaProjektparter.find(
      (p) => p.worksite.id === worksiteId,
    );
    this.setEgenFinansiering(worksiteId);
    if (!inactiveWorksite) {
      this.setEgenFinansieringValidation(worksiteId);
      this.setBudgetIndirektaKostnaderValidation(worksiteId);
      this.setValidation(
        this.kostnaderFormControl.get(worksiteId),
        [BudgetValidation.KostnaderRequired],
        [],
        false,
      );
    }
    this.setBeraknadStodniva(worksiteId);
    this.setBidragsMottagare(worksiteId);
    this.updateBidragsMottagareNotFinansiarForAllControls();
    this.updateValidationForFinancer(worksiteId);
    this.setTotals();
    this.setKonsultkostnaderOverstriden(worksiteId);
    this.setSokerBidrag(worksiteId);
    this.setValue();
  }

  openBudgetImport(): void {
    const currentTables = new Map<string, CurrentBudgetTables>();
    for (const worksiteId of this.kostnaderFormControl.keys()) {
      currentTables.set(worksiteId, {
        kostnader: this.getKostnaderTableValues(worksiteId),
        finansiering: this.getFinansieringTableValues(worksiteId),
      });
    }

    const schema = BudgetImportHelper.buildSchema(
      this.startDate,
      this.endDate,
      // financiers live separately on Projektpart, not on Worksite; attach them so buildSchema can see them.
      {
        ...this.parter.koordinator.worksite,
        financiers: this.parter.koordinator.financiers,
      },
      this.parter.projektparter.map((p) => ({
        ...p.worksite,
        financiers: p.financiers,
      })),
    );

    const modalRef = this.modalService.show(ExcelImportModalShellComponent, {
      initialState: buildBudgetImportBindings(
        schema,
        currentTables,
        this.budgetImportService,
      ),
      backdrop: 'static',
      class: 'modal-xl modal-fullscreen-md-down',
    });
    modalRef.content.applied.subscribe((result) =>
      this.applyBudgetImport(result as BudgetImportApplyResult),
    );
  }

  private applyBudgetImport(result: BudgetImportApplyResult): void {
    const startYear = this.startDate.getFullYear();
    for (const patch of result.projektparter) {
      const kostnaderFc = this.kostnaderFormControl.get(patch.id);
      const finansieringFc = this.finansieringFormControl.get(patch.id);
      if (!kostnaderFc || !finansieringFc) {
        continue;
      }
      const merged = BudgetImportHelper.mergeIntoTables(
        {
          kostnader: this.getKostnaderTableValues(patch.id),
          finansiering: this.getFinansieringTableValues(patch.id),
        },
        patch,
        startYear,
      );
      kostnaderFc.patchValue(JSON.stringify(merged.kostnader));
      finansieringFc.patchValue(JSON.stringify(merged.finansiering));
      kostnaderFc.markAsDirty();
      finansieringFc.markAsDirty();
      this.updateBudgetTables(true, patch.id);
    }
    this.cdr.markForCheck();
  }

  openMasshanteraStodgrund(): void {
    const worksitesObs: Observable<Worksite>[] = [];

    for (const value of this.bidragsMottagare) {
      worksitesObs.push(this.worksiteService.getWorksite(value));
    }

    forkJoin(worksitesObs)
      .pipe(untilDestroyed(this))
      .subscribe((worksites) => {
        const modalRef = this.modalService.show(MasshanteraStodgrundComponent, {
          initialState: { worksites },
          backdrop: 'static',
          class: 'modal-xl modal-fullscreen-md-down',
        });

        modalRef.content.changedStodgrund.subscribe((val) =>
          this.setMasshanteradStodgrund(
            val.worksites,
            val.stodgrundId,
            val.sfsStodgrundId,
          ),
        );
      });
  }

  private setMasshanteradStodgrund(
    worksites: string[],
    stodgrundId: string,
    sfsStodgrundId: string,
  ): void {
    for (const worksiteId of worksites) {
      this.stodgrund.set(worksiteId, stodgrundId);
      this.sfsStodgrund.set(worksiteId, sfsStodgrundId);
      this.cdr.markForCheck();
    }
    this.setValue();
  }

  maxStodnivaChange(worksiteId, value: number): void {
    this.maxStodniva.set(worksiteId, value);
    this.hasMaxStodnivaChanged(worksiteId);
    this.setValue();
  }

  stodgrundChange(worksiteId: string, value: string): void {
    this.sfsStodgrund.set(worksiteId, undefined);
    this.stodgrund.set(worksiteId, value);
    this.hasStodgrundChanged(worksiteId);
    this.setValue();
  }

  sfsStodgrundChange(worksiteId: string, value: string): void {
    this.sfsStodgrund.set(worksiteId, value);
    this.hasSfsStodgrundChanged(worksiteId);
    this.setValue();
  }

  avrundaStodniva(): void {
    let changed = false;
    for (const worksiteId of this.bidragsMottagare) {
      const stodniva = this.beraknadStodniva.get(worksiteId);
      const prevMaxStodniva = this.maxStodniva.get(worksiteId);
      if (!prevMaxStodniva) {
        this.maxStodniva.set(worksiteId, Math.ceil(stodniva));
        changed = true;
      }
      if (changed) {
        this.setValue();
      }
    }
  }

  getMaxStodnivaValidation(worksiteId: string): string {
    const maxStodniva = this.maxStodniva.get(worksiteId);
    const beraknadStodniva = this.beraknadStodniva.get(worksiteId);
    if (maxStodniva == undefined || maxStodniva.toString() === '') {
      return 'Ange maximal stödnivå';
    }

    if (maxStodniva < beraknadStodniva) {
      return 'Maximal stödnivå får inte vara lägre än beräknad stödnivå';
    }
    if (maxStodniva > 100) {
      return 'Maximal stödnivå får inte vara mer än 100';
    }

    return undefined;
  }

  getStodgrundName(stodgrundId: string): string {
    const stodgrund = this.stodgrunder?.find(
      (s) => s.stodgrundId.toString() === stodgrundId,
    );

    if (!stodgrund) {
      return;
    }
    return this.translocoService.getActiveLang() === 'sv'
      ? stodgrund.stodgrundName
      : stodgrund.stodgrundNameEng;
  }

  getSfsStodgrundName(stodgrundId: string, sfsStodgrund: string): string {
    const stodgrundParent = this.stodgrunder?.find(
      (s) => s.stodgrundId.toString() === stodgrundId,
    );
    if (!stodgrundParent) {
      return;
    }

    const stodgrund = stodgrundParent.paragrafer.find(
      (p) => p.stodgrundId.toString() === sfsStodgrund,
    );
    if (!stodgrund) {
      return;
    }

    return this.translocoService.getActiveLang() === 'sv'
      ? stodgrund.stodgrundName
      : stodgrund.stodgrundNameEng;
  }

  hasBeraknadStodnivaChanged(worksiteId: string): boolean {
    const currentRaw = this.beraknadStodniva.get(worksiteId);
    const current = Math.round(currentRaw * 10) / 10; // Round to 1 decimal
    const original = this.getOriginalBeraknadStodniva$(worksiteId);
    // Avoid false positives when backend data is missing or values aren't computed yet.
    if (current === undefined || original === undefined) {
      return false;
    }
    if (!Number.isFinite(current) || !Number.isFinite(original)) {
      return false;
    }

    return current !== original;
  }

  /**
   * Hämtar ursprunglig beräknad stödnivå för en worksite eller total.
   * Wrapper-funktion som anropar rätt funktion beroende på om worksiteId är 'total'.
   */
  private getOriginalBeraknadStodniva$(worksiteId: string): number | undefined {
    return worksiteId === 'total'
      ? getOriginalTotalBeraknadStodniva({
          effektaBudgetAnswer: this.effektaBudgetAnswer,
          isSharedView: this.isSharedView(),
          isChangeRequestKomplettering:
            this.isChangeRequestKomplettering() ||
            this.isApplicationCompletion(),
        })
      : resolveOriginalFieldValue({
          worksiteId,
          field: 'beraknadStodniva',
          isSharedView: this.isSharedView(),
          isChangeRequestKomplettering:
            this.isChangeRequestKomplettering() ||
            this.isApplicationCompletion(),
          effektaBudgetAnswer: this.effektaBudgetAnswer,
        });
  }

  hasMaxStodnivaChanged(worksiteId: string): boolean {
    const current = this.maxStodniva.get(worksiteId);
    const original = resolveOriginalFieldValue({
      worksiteId,
      field: 'maxStodniva',
      isSharedView: this.isSharedView(),
      isChangeRequestKomplettering:
        this.isChangeRequestKomplettering() || this.isApplicationCompletion(),
      effektaBudgetAnswer: this.effektaBudgetAnswer,
    });
    if (current === undefined || original === undefined) {
      return false;
    }
    const hasChanged = current !== original;
    this.hasMaxStodnivaChanges.set(worksiteId, hasChanged);
    return hasChanged;
  }

  hasStodgrundChanged(worksiteId: string): boolean {
    const current = this.stodgrund.get(worksiteId);
    const original = this.getOriginalStodgrundIds(worksiteId).parentId;
    if (current === undefined || original === undefined) {
      return false;
    }
    const hasChanged = current !== original;
    this.hasStodgrundChanges.set(worksiteId, hasChanged);
    return hasChanged;
  }

  hasSfsStodgrundChanged(worksiteId: string): boolean {
    const current = this.sfsStodgrund.get(worksiteId);
    const original = this.getOriginalStodgrundIds(worksiteId).sfsParagraphId;
    if (current === undefined || original === undefined) {
      return false;
    }
    const hasChanged = current !== original;
    this.hasSfsStodgrundChanges.set(worksiteId, hasChanged);
    return hasChanged;
  }

  private getOriginalStodgrundIds(worksiteId: string): {
    rawId: string | undefined;
    parentId: string | undefined;
    sfsParagraphId: string | undefined;
  } {
    const originalRaw = resolveOriginalFieldValue({
      worksiteId,
      field: StodgrundFieldName.stodgrund,
      isSharedView: this.isSharedView(),
      isChangeRequestKomplettering:
        this.isChangeRequestKomplettering() || this.isApplicationCompletion(),
      effektaBudgetAnswer: this.effektaBudgetAnswer,
    });

    const rawId =
      originalRaw === undefined ? undefined : originalRaw.toString();
    if (!rawId) {
      return {
        rawId: undefined,
        parentId: undefined,
        sfsParagraphId: undefined,
      };
    }

    const parent = this.stodgrunder?.find((s) =>
      s.paragrafer?.some((p) => p.stodgrundId.toString() === rawId),
    );

    const isParagraph = Boolean(parent);
    return {
      rawId,
      parentId: parent ? parent.stodgrundId.toString() : rawId,
      sfsParagraphId: isParagraph ? rawId : undefined,
    };
  }

  private getChangeMarkTooltipFormatting(
    worksiteId: string,
    field: StodgrundField,
  ): {
    valueFormatter?: (rawValue: string) => string;
    previousValue?: string;
  } {
    const valueFormatter =
      field === StodgrundFieldName.stodgrund
        ? (rawValue: string) =>
            resolveStodgrundTooltipValue({
              stodGrundId: rawValue,
              stodgrunder: this.stodgrunder,
              resolveNameById: (id: string) => this.getStodgrundName(id),
            })
        : field === StodgrundFieldName.sfsStodgrund
          ? (rawValue: string) => {
              const parent = this.stodgrunder?.find((s) =>
                s.paragrafer?.some(
                  (p) => p.stodgrundId.toString() === rawValue,
                ),
              );
              const resolved = parent
                ? this.getSfsStodgrundName(
                    parent.stodgrundId.toString(),
                    rawValue,
                  )
                : undefined;
              return resolved ?? rawValue;
            }
          : undefined;

    const previousValue =
      field === StodgrundFieldName.stodgrund
        ? (() => {
            const originalParentId =
              this.getOriginalStodgrundIds(worksiteId).parentId;
            if (!originalParentId) {
              return;
            }
            return this.getStodgrundName(originalParentId) ?? originalParentId;
          })()
        : field === StodgrundFieldName.sfsStodgrund
          ? (() => {
              const originalParagraphId =
                this.getOriginalStodgrundIds(worksiteId).sfsParagraphId;
              if (!originalParagraphId) {
                return;
              }

              const parent = this.stodgrunder?.find((s) =>
                s.paragrafer?.some(
                  (p) => p.stodgrundId.toString() === originalParagraphId,
                ),
              );
              const resolved = parent
                ? this.getSfsStodgrundName(
                    parent.stodgrundId.toString(),
                    originalParagraphId,
                  )
                : undefined;

              return resolved ?? originalParagraphId;
            })()
          : undefined;

    return { valueFormatter, previousValue };
  }

  getChangeMarkTooltipHtml(
    worksiteId: string,
    field: StodgrundField,
  ): string | undefined {
    const meta = this.getChangeTooltipMeta();

    const { valueFormatter, previousValue } =
      this.getChangeMarkTooltipFormatting(worksiteId, field);

    return buildChangeMarkTooltipHtml({
      worksiteId,
      field,
      effektaBudgetAnswer: this.effektaBudgetAnswer,
      activeLang: this.translocoService.getActiveLang(),
      previousValueLabel: meta?.text,
      previousValueDate: this.senastBeslut,
      previousValue,
      valueFormatter,
    });
  }

  // För aria description
  getChangeMarkTooltipDescription(
    worksiteId: string,
    field: StodgrundField,
  ): string | undefined {
    const { valueFormatter, previousValue } =
      this.getChangeMarkTooltipFormatting(worksiteId, field);

    return buildChangeMarkTooltipHtml({
      worksiteId,
      field,
      effektaBudgetAnswer: this.effektaBudgetAnswer,
      activeLang: this.translocoService.getActiveLang(),
      output: 'description',
      previousValueDate: this.senastBeslut,
      previousValue,
      valueFormatter,
    });
  }

  private getChangeTooltipMeta():
    | {
        text: string;
        tooltipTextKey:
          | 'ansokan_budget_tooltip'
          | 'begaran_budget_tooltip'
          | 'budget_tooltip_vinnova_change';
      }
    | undefined {
    return resolveChangeTooltipMeta({
      translocoService: this.translocoService,
      isSharedView: this.isSharedView() || this.isApplicationCompletion(),
      senastBeslut: this.senastBeslut,
      isEffektaSharedView: isEffektaSharedView(),
    });
  }

  hasSoektBidrag(worksiteId: string): boolean {
    const finansiering = this.getFinansieringTableValues(worksiteId).filter(
      (f) => f.rowId === '1',
    );
    return finansiering.some((f) => f.value !== 0);
  }

  private setSokerBidrag(worksiteId: string) {
    if (
      this.hasSoektBidrag(worksiteId) &&
      !this.isSharedView() &&
      !this.isChangeRequest()
    ) {
      this.sokerBidragIckeEkonomiskVerksamhetControl.get(worksiteId).enable();
    } else {
      this.sokerBidragIckeEkonomiskVerksamhetControl
        .get(worksiteId)
        .setValue(undefined);
      this.sokerBidragIckeEkonomiskVerksamhetControl.get(worksiteId).disable();
    }
  }

  private setKonsultkostnaderOverstriden(worksiteId: string) {
    const isKonsultkostnadOverskriden =
      this.aerKonsultkostnaderOverstriden(worksiteId);
    this.isKonsultkostnadOverskriden
      .get(worksiteId)
      .set(isKonsultkostnadOverskriden);

    if (
      isKonsultkostnadOverskriden &&
      !this.isSharedView() &&
      !this.isChangeRequest()
    ) {
      this.konsultkostnadOverskridenForklaringControl.get(worksiteId).enable();
    } else {
      this.konsultkostnadOverskridenForklaringControl
        .get(worksiteId)
        .setValue('');
      this.konsultkostnadOverskridenForklaringControl.get(worksiteId).disable();
    }
  }

  private aerKonsultkostnaderOverstriden(worksiteId: string) {
    const kostnader = this.getKostnaderTableValues(worksiteId);

    let totalKostnad = 0;
    let totalKonsultKostnad = 0;
    for (const year of this.yearColumns) {
      for (const kostnad of kostnader.filter((k) => k.columnId === year.id)) {
        totalKostnad += kostnad.value;
      }
      totalKonsultKostnad += kostnader
        .filter(
          (k) =>
            k.rowId === Kostnadstyp.KONSULTKOSTNADER && k.columnId === year.id,
        )
        .reduce((a: number, b) => a + b.value, 0);
    }

    return totalKonsultKostnad > totalKostnad * 0.2;
  }

  private setStodgrundItems(lang: string): void {
    const stodgrunder = this.stodgrunder?.filter((item) => {
      const stodgrundChoosen = [...this.stodgrund.values()]?.some(
        (stodgrundId) => parseInt(stodgrundId) === item.stodgrundId,
      );
      if ((stodgrundChoosen && !item.aktiv) || item.aktiv) {
        return item;
      }
    });

    this.stodgrunderItems = stodgrunder?.map((s) => {
      return {
        label: lang === 'sv' ? s.stodgrundName : s.stodgrundNameEng,
        value: s.stodgrundId.toString(),
        disabled: !s.aktiv,
      };
    });
  }

  private setSfsStodgrundItems(lang: string): void {
    if (this.stodgrunder) {
      const stodgrunder = this.stodgrunder?.filter((item) => {
        const stodgrundChoosen = [...this.stodgrund.values()].some(
          (stodgrundId) => parseInt(stodgrundId) === item.stodgrundId,
        );
        if ((stodgrundChoosen && !item.aktiv) || item.aktiv) {
          return item;
        }
      });

      this.sfsStodgrunderItems = {};
      for (const stodgrund of stodgrunder) {
        this.sfsStodgrunderItems[stodgrund.stodgrundId.toString()] =
          stodgrund.paragrafer.map((p) => {
            return {
              label: lang === 'sv' ? p.stodgrundName : p.stodgrundNameEng,
              value: p.stodgrundId.toString(),
              disabled: !p.aktiv,
            };
          });
      }
    }
  }

  private setBeraknadStodniva(worksiteId: string): void {
    const kostnader: TableRowValueList =
      this.getKostnaderTableValues(worksiteId);
    const finansiering: TableRowValueList =
      this.getFinansieringTableValues(worksiteId);

    const soktBidragYears = finansiering.filter((f) => f.rowId === '1');
    let soktBidragTotal = 0;
    for (const soktBidragYear of soktBidragYears) {
      soktBidragTotal += soktBidragYear.value;
    }

    let kostnaderTotal = 0;
    for (const kostnad of kostnader) {
      kostnaderTotal += kostnad.value;
    }

    this.beraknadStodniva.set(
      worksiteId,
      kostnaderTotal > 0 ? (soktBidragTotal / kostnaderTotal) * 100 : 0,
    );
  }

  private setEgenFinansiering(worksiteId: string): void {
    const kostnader: TableRowValueList =
      this.getKostnaderTableValues(worksiteId);
    const finansiering: TableRowValueList =
      this.getFinansieringTableValues(worksiteId);
    const egenFinansiering: TableRowValueList = [];

    for (const year of this.yearColumns) {
      let totalKostnadPerYear = 0;
      let totalFinansieringPerYear = 0;
      for (const kostnad of kostnader.filter((k) => k.columnId === year.id)) {
        totalKostnadPerYear += kostnad.value;
      }

      for (const finansieringPost of finansiering.filter(
        (f) => f.columnId === year.id,
      )) {
        totalFinansieringPerYear += finansieringPost.value;
      }

      egenFinansiering.push({
        rowId: Finansieringstyp.EGENFINANSIERING,
        columnId: year.id,
        value: totalKostnadPerYear - totalFinansieringPerYear,
      });
    }

    this.egenFinansiering.set(worksiteId, egenFinansiering);
  }

  private setEgenFinansieringValidation(worksiteId: string): void {
    const kostnader: TableRowValueList =
      this.getKostnaderTableValues(worksiteId);
    const kostnaderMapped = kostnader.map((a) => a.value);
    const totalKostnader = kostnaderMapped.reduce((a, b) => a + b, 0);

    const egenFinansieringValidation =
      this.finansieringRowValidation.get(worksiteId);
    egenFinansieringValidation.find(
      (a) => a.validation === BudgetValidation.EgenFinansieringRequired,
    ).arg = totalKostnader;
    this.finansieringRowValidation.set(worksiteId, [
      ...egenFinansieringValidation,
    ]);
    const control = this.finansieringFormControl.get(worksiteId);
    this.setValidation(control, [], egenFinansieringValidation, true);
  }

  private setBudgetIndirektaKostnaderValidation(worksiteId: string): void {
    const indirektaKostnaderValidation =
      this.kostnaderRowValidation.get(worksiteId);

    this.setValidation(
      this.kostnaderFormControl.get(worksiteId),
      [],
      indirektaKostnaderValidation,
      true,
    );
  }

  private getKostnaderTableValues(worksiteId: string): TableRowValueList {
    return this.kostnaderFormControl.get(worksiteId).value
      ? JSON.parse(this.kostnaderFormControl.get(worksiteId).value)
      : [];
  }

  private getFinansieringTableValues(worksiteId: string): TableRowValueList {
    return this.finansieringFormControl.get(worksiteId).value
      ? JSON.parse(this.finansieringFormControl.get(worksiteId).value)
      : [];
  }

  private setTotals(): void {
    const newTotalKostnader = [];
    const newTotalFinansiering = [];
    this.totalColumnsAnnanFinansiering = [];
    if (this.budgetStandard()) {
      for (const year of this.yearColumns) {
        newTotalKostnader.push({
          rowId: Standardkostnadstyp.TOTALKOSTNAD,
          columnId: year.id,
          value: 0,
        });
      }
    } else {
      for (const row of this.KOSTNADSSLAG) {
        for (const year of this.yearColumns) {
          newTotalKostnader.push({
            rowId: row.id,
            columnId: year.id,
            value: 0,
          });
        }
      }
    }

    for (const row of this.FINANSIERING) {
      for (const year of this.yearColumns) {
        newTotalFinansiering.push({
          rowId: row.id,
          columnId: year.id,
          value: 0,
        });
      }
    }

    let kostnaderTotal = 0;
    for (const kostnad of this.kostnaderFormControl.entries()) {
      const parsed = (kostnad[1] as FormControl<string>).value
        ? JSON.parse((kostnad[1] as FormControl<string>).value)
        : [];
      const rowSums = Array.from({ length: this.KOSTNADSSLAG.length }, () => 0);
      const colSums = Array.from({ length: this.yearColumns.length }, () => 0);
      let totalSum = 0;
      for (const tableData of parsed) {
        rowSums[+tableData.rowId - 1] += +tableData.value;
        colSums[+tableData.columnId] += +tableData.value;
        totalSum += +tableData.value;
        const kostnadItem = newTotalKostnader.find(
          (t) =>
            t.rowId === tableData.rowId && t.columnId === tableData.columnId,
        );
        if (kostnadItem) {
          kostnadItem.value = kostnadItem.value + tableData.value;
          kostnaderTotal += tableData.value;
        }
      }
      this.projektpartKostnader.set(kostnad[0], {
        rows: rowSums,
        columns: colSums,
        totalSum: totalSum,
      });
    }

    let soktBidragTotal = 0;
    for (const finansiering of this.finansieringFormControl.entries()) {
      const parsed = (finansiering[1] as FormControl<string>).value
        ? JSON.parse((finansiering[1] as FormControl<string>).value)
        : [];
      const rowSums = Array.from(
        { length: this.finansieringRows.get(finansiering[0])?.length },
        () => 0,
      );
      for (const tableData of parsed) {
        rowSums[+tableData.rowId - 1] += +tableData.value;
        const rowId = this.finansiarer.includes(tableData.rowId)
          ? Finansieringstyp.ANNANFINANSIERING
          : tableData.rowId;

        const finansieringItem = newTotalFinansiering.find(
          (t) => t.rowId === rowId && t.columnId === tableData.columnId,
        );
        if (finansieringItem) {
          finansieringItem.value = finansieringItem.value + tableData.value;

          if (tableData.rowId === Finansieringstyp.VINNOVABIDRAG) {
            soktBidragTotal += tableData.value;
          }
        }
      }
      this.projektpartFinansiering.set(finansiering[0], {
        rows: rowSums,
        columns: [],
        totalSum: 0,
      });
    }
    for (const egenFinansieringPart of this.egenFinansiering) {
      let totalRowSum = 0;
      for (const egenFinansieringYear of egenFinansieringPart[1]) {
        const finansieringItem = newTotalFinansiering.find(
          (t) =>
            t.rowId === egenFinansieringYear.rowId &&
            t.columnId === egenFinansieringYear.columnId,
        );
        if (finansieringItem) {
          finansieringItem.value =
            finansieringItem.value + egenFinansieringYear.value;
        }
        totalRowSum += egenFinansieringYear.value;
      }
      this.projektpartEgenFinansiering.set(egenFinansieringPart[0], {
        totalRowSum: totalRowSum,
      });
    }

    const stodniva =
      kostnaderTotal > 0 ? (soktBidragTotal / kostnaderTotal) * 100 : 0;

    this.beraknadStodniva.set('total', stodniva);
    this.questionForm().get('stodniva').patchValue(stodniva);
    this.questionForm().get('soktBidrag').patchValue(soktBidragTotal);

    this.totalKostnader = newTotalKostnader;
    this.totalFinansiering = newTotalFinansiering;
    this.totalFinansieringsSumma = 0;
    this.totalOtherFinansiering = 0;
    this.totalEgenFinansiering = 0;
    this.setTotalTableSums();
    this.totalColumnsEgenFinansiering = [];
    for (const item of newTotalFinansiering) {
      if (item.rowId === '2') {
        if (this.totalColumnsAnnanFinansiering[item.columnId]) {
          this.totalColumnsAnnanFinansiering[item.columnId] += item.value;
        } else {
          this.totalColumnsAnnanFinansiering[item.columnId] = item.value;
        }
        this.totalOtherFinansiering += item.value;
      }
      if (item.rowId === '3') {
        if (this.totalColumnsEgenFinansiering[item.columnId]) {
          this.totalColumnsEgenFinansiering[item.columnId] += item.value;
        } else {
          this.totalColumnsEgenFinansiering[item.columnId] = item.value;
        }
        this.totalEgenFinansiering += item.value;
      }
      this.totalFinansieringsSumma += item.value;
    }
    this.cdr.markForCheck();
  }

  private setBidragsMottagare(worksiteId: string): void {
    const finansiering = this.getFinansieringTableValues(worksiteId);

    const bidragsMottagare = finansiering
      .filter((f) => f.rowId === Finansieringstyp.VINNOVABIDRAG)
      .some((f) => f.value > 0);
    if (bidragsMottagare && !this.bidragsMottagare.has(worksiteId)) {
      this.bidragsMottagare.add(worksiteId);
    } else if (!bidragsMottagare && this.bidragsMottagare.has(worksiteId)) {
      this.bidragsMottagare.delete(worksiteId);
    }
  }

  private setValue(isFirst = false): void {
    const jsonValue: IProjektBudget = {} as IProjektBudget;

    if (this.startDate && this.endDate && this.parter?.koordinator) {
      jsonValue.startDatum = formatISO(this.startDate);
      jsonValue.slutDatum = formatISO(this.endDate);
      jsonValue.projektparterInvalid = false;
      jsonValue.projektparter = [];

      if (this.budgetStandard()) {
        jsonValue.budgetNiva = this.effektaBudgetAnswer?.budgetNiva;
      }
      this.totalKostnaderChanges = [];
      this.totalFinansieringChanges = [];
      this.resetTotalTablesChanges();
      jsonValue.projektparter.push(
        this.createProjektpartJsonValue(this.parter.koordinator, true),
      );

      for (const part of this.parter.projektparter) {
        jsonValue.projektparter.push(
          this.createProjektpartJsonValue(part, false),
        );
      }
      for (const part of this.inaktivaProjektparter) {
        jsonValue.projektparter.push(
          this.createProjektpartJsonValue(part, false),
        );
      }
      const hasChanges = this.hasBudgetOrProjektparterChanges();

      jsonValue.hasChanges = hasChanges;

      this.questionForm().get('value').patchValue(JSON.stringify(jsonValue));
      if (!isFirst) {
        this.questionForm().markAsTouched();
        this.questionForm().get('value').markAsDirty();
      }
    } else if (this.projektpartInvalid && this.questionForm().value?.value) {
      const oldValue = this.questionForm().value.value;
      const oldJsonValue = JSON.parse(oldValue);
      oldJsonValue.projektParterInvalid = true;

      this.questionForm().get('value').patchValue(JSON.stringify(oldJsonValue));
      this.questionForm().markAsTouched();
      this.questionForm().get('value').markAsDirty();
    }
    this.rendererFormRepository.setShowChangesAnswers(
      'budget',
      [...this.hasCostChanges.values()].some(Boolean) ||
        [...this.hasFinansieringChanges.values()].some(Boolean) ||
        [...this.hasMaxStodnivaChanges.values()].some(Boolean) ||
        [...this.hasStodgrundChanges.values()].some(Boolean) ||
        [...this.hasSfsStodgrundChanges.values()].some(Boolean) ||
        this.newYearColumnCount > 0,
    );

    this.formValidationService.triggerSectionInvalidCheck(this.question().id);
  }

  private resetTotalTablesChanges() {
    for (let i = 0; i <= this.KOSTNADSSLAG.length; i++) {
      this.totalKostnaderChanges[i] = [];
    }
    for (let i = 0; i <= this.FINANSIERING.length; i++) {
      this.totalFinansieringChanges[i] = [];
    }
  }

  private createProjektpartJsonValue(
    projektpart: Projektpart,
    coordinator: boolean,
  ): IProjektpart {
    const projektpartJsonValue: IProjektpart = {} as IProjektpart;
    projektpartJsonValue.id = projektpart.worksite.id;
    projektpartJsonValue.koordinerande = coordinator;
    projektpartJsonValue.kostnader = [];
    projektpartJsonValue.bidragPerAr = [];
    projektpartJsonValue.finansiarer = [];
    projektpartJsonValue.kostnaderChanges = [];
    projektpartJsonValue.finansieringChanges = [];

    const kostnader = this.getKostnaderTableValues(projektpart.worksite.id);
    const finansiering = this.getFinansieringTableValues(
      projektpart.worksite.id,
    );

    const konsultkostnadOverskridenForklaring =
      this.konsultkostnadOverskridenForklaringControl.get(
        projektpart.worksite.id,
      ).value;

    const sokerBidragIckeEkonomiskVerksamhet =
      this.sokerBidragIckeEkonomiskVerksamhetControl.get(
        projektpart.worksite.id,
      ).value;

    for (const kostnadsslag of this.KOSTNADSSLAG) {
      const kostnadsslagJsonValue: Kostnadsslag = {} as Kostnadsslag;

      kostnadsslagJsonValue.kostnadsslag = +kostnadsslag.id;
      kostnadsslagJsonValue.kostnaderPerAr = [];

      for (const year of this.yearColumns) {
        const kostnadsslagYearJsonValue: SummaPerAr = {} as SummaPerAr;
        const kostnad =
          kostnader.find(
            (k) => k.rowId === kostnadsslag.id && k.columnId === year.id,
          )?.value ?? 0;
        kostnadsslagYearJsonValue.ar = +year.title;
        kostnadsslagYearJsonValue.summa = kostnad;
        kostnadsslagJsonValue.kostnaderPerAr.push(kostnadsslagYearJsonValue);
      }
      projektpartJsonValue.kostnader.push(kostnadsslagJsonValue);
      if (this.budgetStandard()) {
        break;
      }
    }
    for (const year of this.yearColumns) {
      const bidragYearJsonValue: SummaPerAr = {} as SummaPerAr;
      const bidrag =
        finansiering.find(
          (f) =>
            f.rowId === Finansieringstyp.VINNOVABIDRAG &&
            f.columnId === year.id,
        )?.value ?? 0;
      bidragYearJsonValue.ar = +year.title;
      bidragYearJsonValue.summa = bidrag;
      projektpartJsonValue.bidragPerAr.push(bidragYearJsonValue);
    }
    for (const finansiar of projektpart.financiers) {
      const finansiarJsonValue: Finansiar = {} as Finansiar;
      finansiarJsonValue.id = finansiar.id;
      finansiarJsonValue.finansieringPerAr = [];
      for (const year of this.yearColumns) {
        const finansieringYearJsonValue: SummaPerAr = {} as SummaPerAr;
        const finansiarSumma =
          finansiering.find(
            (f) => f.rowId === finansiar.id && f.columnId === year.id,
          )?.value ?? 0;
        finansieringYearJsonValue.ar = +year.title;
        finansieringYearJsonValue.summa = finansiarSumma;
        finansiarJsonValue.finansieringPerAr.push(finansieringYearJsonValue);
      }
      projektpartJsonValue.finansiarer.push(finansiarJsonValue);
    }

    if (
      this.isSharedView() ||
      this.isChangeRequest() ||
      this.isApplicationCompletion()
    ) {
      const maxStodniva = this.maxStodniva.get(projektpart.worksite.id);
      const stodgrund = this.stodgrund.get(projektpart.worksite.id);
      const sfsStodgrund = this.sfsStodgrund.get(projektpart.worksite.id);

      projektpartJsonValue.maxStodniva = {
        varde: maxStodniva,
        vardeDecimal: maxStodniva,
        andringsmarkering: [],
      };

      projektpartJsonValue.stodgrund = sfsStodgrund
        ? { varde: parseInt(sfsStodgrund) }
        : stodgrund
          ? { varde: parseInt(stodgrund) }
          : undefined;

      const projektpartValue = this.getProjektPartValue(projektpart);

      const effektaPartValue = this.effektaBudgetAnswer?.projektparter?.find(
        (p) => p.id === projektpart.worksite.id,
      );

      if (
        this.isChangeRequest() ||
        this.isSharedView() ||
        this.isApplicationCompletion()
      ) {
        this.checkFinancingChanges(
          effektaPartValue,
          finansiering,
          projektpartValue,
        );

        this.checkCostChanges(effektaPartValue, kostnader, projektpartValue);

        if (effektaPartValue === undefined && projektpartValue) {
          projektpartValue.isNew = true;
          this.checkNewProjectPartChanges(
            kostnader,
            finansiering,
            projektpartValue,
          );
        }
      }

      this.setChanges(projektpart, projektpartValue);
      if (projektpartValue) {
        projektpartJsonValue.kostnaderChanges =
          projektpartValue.kostnaderChanges;
        projektpartJsonValue.finansieringChanges =
          projektpartValue.finansieringChanges;
      }
    }

    if (konsultkostnadOverskridenForklaring) {
      projektpartJsonValue.konsultkostnadOverskridenForklaring =
        konsultkostnadOverskridenForklaring;
    }

    if (sokerBidragIckeEkonomiskVerksamhet) {
      projektpartJsonValue.sokerBidragIckeEkonomiskVerksamhet =
        sokerBidragIckeEkonomiskVerksamhet;
    }
    return projektpartJsonValue;
  }

  private getKostnadValueForWorksite(projektpartValue: IProjektpart): string {
    const kostnadValue: TableRowValueList = [];
    if (this.budgetStandard()) {
      for (const year of projektpartValue.kostnader[0].kostnaderPerAr) {
        kostnadValue.push({
          rowId: Standardkostnadstyp.TOTALKOSTNAD,
          columnId: (year.ar - this.startDate.getFullYear()).toString(),
          value: year.summa,
        });
      }
    } else {
      for (const kostnadsslag of projektpartValue.kostnader) {
        for (const year of kostnadsslag.kostnaderPerAr) {
          kostnadValue.push({
            rowId: kostnadsslag.kostnadsslag?.toString(),
            columnId: (year.ar - this.startDate?.getFullYear())?.toString(),
            value: year.summa,
          });
        }
      }
    }

    return JSON.stringify(kostnadValue);
  }

  private getFinansieringValueForWorksite(
    projektpartValue: IProjektpart,
  ): string {
    const finansieringValue: TableRowValueList = [];

    if (projektpartValue.bidragPerAr) {
      for (const year of projektpartValue.bidragPerAr) {
        finansieringValue.push({
          rowId: Finansieringstyp.VINNOVABIDRAG,
          columnId: (year.ar - this.startDate.getFullYear()).toString(),
          value: year.summa,
        });
      }
    }
    if (projektpartValue.finansiarer) {
      for (const finansiar of projektpartValue.finansiarer) {
        if (!this.finansiarer.includes(finansiar.id)) {
          continue;
        }
        for (const year of finansiar.finansieringPerAr) {
          finansieringValue.push({
            rowId: finansiar.id,
            columnId: (year.ar - this.startDate.getFullYear()).toString(),
            value: year.summa,
          });
        }
      }
    }

    return JSON.stringify(finansieringValue);
  }

  private setValidation(
    control: FormControl<string>,
    validations: string[],
    rowValidations: RowValidationList,
    clearValidators: boolean,
  ): void {
    if (clearValidators) {
      control.clearValidators();
    }
    if (validations.includes(BudgetValidation.KostnaderRequired)) {
      control.addValidators(BudgetKostnaderRequired);
    }

    if (rowValidations) {
      for (const rowValidation of rowValidations) {
        if (rowValidation.validation === BudgetValidation.FinansiarRequired) {
          control.addValidators(BudgetFinansiarRequired(rowValidation.rowId));
        }
        if (
          rowValidation.validation === BudgetValidation.EgenFinansieringRequired
        ) {
          control.addValidators(
            BudgetEgenFinansieringRequired(
              rowValidation.rowId,
              rowValidation.arg,
            ),
          );
        }
        if (rowValidation.validation === BudgetValidation.Under10KWarning) {
          control.addValidators(Budget10kWarning(rowValidation.rowId));
        }
        if (
          rowValidation.validation === BudgetValidation.MottagareNotFinansiar
        ) {
          control.addValidators(
            BudgetMottagareNotFinansiar(this.bidragsMottagare),
          );
        }
        if (
          rowValidation.validation ===
          BudgetValidation.IndirektaKostnaderOverLonekostnader
        ) {
          control.addValidators(BudgetIndirektaKostnaderWarning());
        }
      }
    }
    control.updateValueAndValidity();
  }

  private setTableHeadersChanges(tables: BudgetTableChanges[]): void {
    const parsedValue = this.value.value
      ? JSON.parse(this.value.value)
      : undefined;

    let yearsToAppendCount = 0;
    if (this.isChangeRequest() && this.newProjektEndDate) {
      yearsToAppendCount = calculateYearDifference(
        new Date(this.projektEndDate),
        this.newProjektEndDate,
      );
    } else if (this.effektaBudgetAnswer?.gamlaSlutAret) {
      yearsToAppendCount = calculateYearDifference(
        this.effektaBudgetAnswer.gamlaSlutAret,
        new Date(parsedValue.slutDatum),
      );
    }

    let yearsToInsertCount = 0;
    if (this.isChangeRequest() && this.newProjektStartDate) {
      yearsToInsertCount = calculateYearDifference(
        this.newProjektStartDate,
        new Date(this.projektStartDate),
      );
    } else if (this.effektaBudgetAnswer?.gamlaStartAret) {
      yearsToInsertCount = calculateYearDifference(
        new Date(parsedValue.startDatum),
        this.effektaBudgetAnswer.gamlaStartAret,
      );
    }

    this.newYearColumnCount = yearsToInsertCount + yearsToAppendCount;

    if (this.newYearColumnCount > 0) {
      pushTableHeadersWithChanges(tables, yearsToInsertCount);
      const lastColumnIndex = this.yearColumns.length - 1;
      insertTableHeadersWithChanges(
        tables,
        lastColumnIndex,
        yearsToAppendCount,
      );
    }
  }

  onKeydownMaxStodgrund(event: KeyboardEvent): void {
    preventNonIntegerInput(event);
  }

  private hasBudgetOrProjektparterChanges(): boolean {
    const costChanged = [...this.hasCostChanges.values()].some(Boolean);
    const financingChanged = [...this.hasFinansieringChanges.values()].some(
      Boolean,
    );
    const yearChanged = this.newYearColumnCount > 0;
    const projektpartAdded = this.newProjektparter.size > 0;
    const projektpartRemoved = this.removedProjektparter.size > 0;

    return (
      costChanged ||
      financingChanged ||
      yearChanged ||
      projektpartAdded ||
      projektpartRemoved
    );
  }

  toggleSection(worksiteId: string) {
    const wasOpen = this.isOpen.get(worksiteId);
    this.isOpen.set(worksiteId, !wasOpen);

    if (wasOpen) {
      this.stickyHeaderObserver.scrollToElementWithOffset(
        `[aria-controls="projektpart-${worksiteId}"]`,
      );
    }
  }

  ngAfterViewInit() {
    this.setupStickyHeaderObserver();
  }

  private setupStickyHeaderObserver() {
    this.stickyHeaderObserver = new StickyHeaderObserver({
      isSmallWidth: this.isSmallWidth,
      isSharedView: this.isSharedView(),
      onScreenSizeChange: (isSmallWidth: boolean) => {
        this.isSmallWidth = isSmallWidth;
      },
    });
  }

  private checkIfStodgrundOrMaxStodnivaHasChanged() {
    for (const projektpart of this.effektaBudgetAnswer.projektparter) {
      this.hasMaxStodnivaChanged(projektpart.id);
      this.hasStodgrundChanged(projektpart.id);
      this.hasSfsStodgrundChanged(projektpart.id);
    }
  }
}
